// ─────────────────────────────────────────────────────────────────────────────
// Organisation settings — company-wide configuration (admin module).
// Faithful port of the Claude Design prototype (screens_org.jsx + screens_org_roles.jsx):
// Office hours · yearly holidays · global leave policy · leave types · roles & access.
// Backed by Supabase: org_settings (singleton), holidays, leave_types, roles,
// role_permissions (the live RBAC matrix that gates the whole app).
// ─────────────────────────────────────────────────────────────────────────────
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useBelow, useMediaQuery } from '../lib/useMediaQuery';
import { supabase } from '../lib/supabase';
import { hhmmToMin, orgHoursFrom } from '../lib/orgHours';
import { createBurst, pick, type Burst } from '../lib/burst';
import { useAuth } from '../lib/auth';
import { can, isModuleEnabled, MODULES, ROLE_META, roleTone, type PermLevel } from '../lib/roles';
import { usePageCrumbs } from '../components/shell';
import { ConfirmDialog, usePopover } from '../components/dialog';
import {
  Avatar,
  AttentionCount,
  Badge,
  Button,
  Checkbox,
  Icon,
  IconButton,
  Mono,
  Select,
  Tabs,
  Toggle,
  TONE,
  type ToneName,
  type SelectOption,
  type IconName,
} from '../components/primitives';
import { useAttention } from '../lib/attention';
import { EmptyState, LoadError, useToast, type ToastInput } from '../components/feedback';
import { write, type WriteResult } from '../lib/write';
import { EditableField, MONTHS, PCard, Stepper } from '../components/people';
import { DisclosedForm, Eyebrow, PeriodStepper, SegmentedControl } from '../components/card';
import { DayMark, DayMarkKey, MonthGrid } from '../components/monthGrid';
import { isWorkday } from '../lib/workdays';
import { DateField, Field, TextArea, TextInput, type SaveResult } from '../components/field';
import {
  DATE_FORMATS,
  fmtDate,
  fmtDayKey,
  fmtTimeOfDay,
  orgDateKey,
  tzLabel,
  weekStartIndex,
} from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import {
  useOrgDates,
  resetOrgDates,
  DEFAULT_EVENT_TYPES,
  type EventType,
} from '../lib/orgSettings';
import { resetDepartments, type Department } from '../lib/departments';
import { DEFAULT_CAPACITY_VISIBILITY, type CapacityVisibility } from '../lib/capacity';
import { DEFAULT_TARGET_UTILISATION } from '../lib/finance';
import { revokeAllLinks } from '../lib/calendarLinks';
import type { EditDayRollout } from '../lib/editDay';
import { DashboardAccess } from '../components/dashboardAccess';
import {
  HOLIDAY_KINDS as ORG_HOLIDAY_KINDS,
  type Holiday,
  type HolidayKind,
} from '../lib/holidays';

// The org_settings columns useOrgDates() caches. Editing any of them must drop
// that cache or the rest of the app keeps the old answer until a reload. One
// list, because the two call sites in patchOrg previously spelled it out twice
// and would have silently missed the hours columns when they were added.
const CACHED_ORG_KEYS = [
  'week_start',
  'tz_iana',
  'date_format',
  'day_start',
  'day_end',
  'lunch_mins',
  'workdays',
  // policy joined the cached set when useOrgDates began serving
  // policy.employment to the invite modal. Without it here, editing the
  // employment standards leaves every open screen on the old values.
  'policy',
] as const;

// Yearly-recurring holiday: the same month-day in `date`'s year plus the next `n`,
// skipping Feb-29 in non-leap years and any date already in `existing`.
export function yearlyHolidayDates(
  date: string,
  existing: Set<string> = new Set(),
  n = 4,
): string[] {
  const md = date.slice(4); // '-MM-DD'
  const y0 = Number(date.slice(0, 4));
  const out: string[] = [];
  for (let y = y0; y <= y0 + n; y++) {
    const ds = `${y}${md}`;
    if (new Date(`${ds}T00:00:00`).getMonth() + 1 !== Number(ds.slice(5, 7))) continue; // Feb-29 in non-leap
    if (existing.has(ds)) continue;
    out.push(ds);
  }
  return out;
}

// ── domain types ──────────────────────────────────────────────────────────────
type Workdays = Record<string, boolean>;
type Overtime = {
  enabled: boolean;
  hoursPerLeave: number;
  targetType: string;
  maxPerMonth: number;
  maxPerYear: number;
  countWeekends: boolean;
};
// Working on an approved leave day: how much of the day must be worked before
// the leave is halved / returned. Fractions of the standard workday.
type LeaveClash = {
  enabled: boolean;
  halfDayAt: number;
  cancelAt: number;
};
// R3 — the standards a hire is set up against. Defaults, not constants: any hire
// may deviate, but the deviation is recorded against that person.
type Employment = {
  probation_months: number;
  notice_days_employee: number;
  notice_days_company: number;
  offer_acceptance_days: number;
  email_format: string;
  reminder_lead_days: number;
};
// R1. Only what payroll_run actually reads: pay_day and reconciliation_day
// belong to R6's sweep and would be controls with no reader until then.
type Payroll = {
  // waiveBelowHours: a shortfall strictly below it deducts nothing
  // (20261009110000; default 3).
  deduction: { enabled: boolean; waiveBelowHours: number };
  prorata: string;
};
type Policy = {
  approval: boolean;
  approvalLevels: number;
  overtime: Overtime;
  leaveClash: LeaveClash;
  employment: Employment;
  payroll: Payroll;
  calendar: { types: EventType[]; links: boolean };
  /** Absent until someone saves it: capacity_visibility() then reads the default. */
  capacity?: { visibility: CapacityVisibility };
  /** Target utilisation (%), read by fin_target() (20261104100000_money_utilisation.sql),
   *  which takes an unset or non-numeric value as 80. */
  finance?: { targetUtilisation: number };
  /** FR-8.5: working days a request may wait before sweep_escalations() escalates it.
   *  Absent means 2, the sweep's own coalesce. */
  escalation?: { afterDays: number };
  /** Which time-correction dialog shows (20261103120000). Absent means off. */
  editDay?: { rollout: EditDayRollout };
};
type OrgSettings = {
  id: number;
  name: string;
  legal_name: string | null;
  timezone: string;
  /** IANA id (20260614000000_time_integrity.sql:19). THE column org_today() reads. */
  tz_iana: string;
  week_start: string;
  date_format: string;
  workdays: Workdays;
  day_start: string;
  day_end: string;
  lunch_mins: number;
  core_start: string;
  core_end: string;
  policy: Policy;
};
type LeaveType = {
  id: string;
  label: string;
  short: string;
  color: string;
  paid: boolean;
  days_per_year: number;
  rank: number;
  /** Off every picker, with its history kept. Absent until the column lands
   *  (`archived boolean not null default false`), which reads as not archived. */
  archived?: boolean;
};
type Role = {
  id: string;
  label: string;
  tone: string;
  description: string | null;
  parent_id: string | null;
  is_custom: boolean;
  rank: number;
};
type RolePerm = { role_id: string; module: string; level: PermLevel };

const DEFAULT_POLICY: Policy = {
  approval: true,
  approvalLevels: 1,
  overtime: {
    enabled: true,
    hoursPerLeave: 8,
    targetType: 'casual',
    maxPerMonth: 2,
    maxPerYear: 12,
    countWeekends: false,
  },
  // Mirrors the coalesce defaults in reconcile_leave_clash().
  leaveClash: { enabled: true, halfDayAt: 0.25, cancelAt: 0.75 },
  // Mirrors the column default in 20260828110000_employment_policy.sql.
  employment: {
    probation_months: 3,
    notice_days_employee: 7,
    notice_days_company: 7,
    offer_acceptance_days: 7,
    email_format: '{first}.{last}@hyprflow.com',
    reminder_lead_days: 14,
  },
  // Mirrors the column default in 20260829120000_payroll.sql.
  payroll: { deduction: { enabled: true, waiveBelowHours: 3 }, prorata: 'working_days' },
  // Mirrors the column default in 20260917120000_calendar_events.sql:49-55; links
  // absent means on (20260918100000_calendar_links.sql:52-56).
  calendar: { types: DEFAULT_EVENT_TYPES, links: true },
};

// ── constants ─────────────────────────────────────────────────────────────────
const ORG_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Leave-type colours are stored as hex on leave_types.color, so these stay hex;
// typeSwatch() mixes the wash from whichever one a row holds. Iris matches its
// token, and the pink is the categorical seed's (docs/UI-REMEDIATION-PLAN.md
// §4.1). A leave type is a category, so the picker offers categorical colours
// only (UI-CONVENTIONS §3): it offered rose, green and amber too, which mean
// wrong, done and waiting on every other screen, and the seed painted Sick rose
// and Unpaid amber. Those two moved to pink and blue with the database's own
// recolouring (critique 2026-10-06, minor), from this same list. Coral is a
// regional holiday on the same calendars, and with the seed's four on Sky,
// Lilac, Blue and Pink it was every new type's colour, so Tan (--hf-cat-7)
// takes its place.
const TYPE_COLOURS: { value: string; label: string }[] = [
  { value: '#4CD6FF', label: 'Sky' },
  { value: '#B794F6', label: 'Lilac' },
  { value: '#6E86FF', label: 'Blue' },
  { value: '#C9A27A', label: 'Tan' },
  // The regional holiday's colour too: offered on the owner's call
  // (2026-10-09). After Tan, so a new type still opens on Tan.
  { value: ORG_HOLIDAY_KINDS.regional.color, label: 'Coral' },
  { value: '#F28FD0', label: 'Pink' },
];
const ORG_TYPE_PALETTE = TYPE_COLOURS.map((c) => c.value);
// A row may still hold a hue that means something from before: named, and no
// longer offered.
const RETIRED_COLOURS: Record<string, string> = {
  '#FF6B8A': 'Rose',
  '#5CE4A8': 'Green',
  '#FFB84D': 'Amber',
};
/** The first colour no type in use has; with every one taken, the one after `after`. */
const nextColour = (inUse: string[], after?: string): string =>
  ORG_TYPE_PALETTE.find((c) => !inUse.includes(c)) ??
  ORG_TYPE_PALETTE[(ORG_TYPE_PALETTE.indexOf(after ?? '') + 1) % ORG_TYPE_PALETTE.length];

// Modules whose access each role can be granted — mirrors the Workspace nav so
// role access maps 1:1 to what people actually see. Keys = lib/roles MODULES.
const ORG_MODULE_META: Record<string, { label: string; icon: IconName; desc: string }> = {
  dashboard: { label: 'Dashboard', icon: 'home', desc: 'Workspace overview & KPIs' },
  projects: { label: 'Projects', icon: 'briefcase', desc: 'Project list, tasks & briefs' },
  pipeline: { label: 'Pipeline', icon: 'layers', desc: 'Sales & opportunity pipeline' },
  talent: {
    label: 'Talent',
    icon: 'users',
    desc: 'Talent grid, skills & utilisation · View or higher also opens the whole people directory',
  },
  capacity: { label: 'Capacity', icon: 'layers', desc: 'Workload & availability planning' },
  timesheets: { label: 'Timesheets', icon: 'clock', desc: 'Log, review & approve time' },
  schedule: { label: 'Schedule', icon: 'gantt', desc: 'Gantt & delivery timelines' },
  forecasting: { label: 'Forecasting', icon: 'trending', desc: 'Revenue & resource forecasts' },
  inbox: { label: 'Notifications', icon: 'inbox', desc: 'Workspace → Notifications tab' },
  people: { label: 'People', icon: 'users', desc: 'Directory, records, payroll & leave' },
  contracts: { label: 'Contracts', icon: 'file', desc: 'Client & employment documents' },
  org: { label: 'Organisation', icon: 'settings', desc: 'Company-wide configuration' },
  reports: { label: 'Reports', icon: 'file', desc: 'Daily client status reports' },
  calendar: {
    label: 'Calendar',
    icon: 'calendar',
    desc: 'View: company & own-department events · Edit: sees every event, can’t post · Full: posts, edits & deletes events',
  },
};

// Access levels, ascending. The ink draws one line, between no access and
// some: None is dimmed, every other level is full ink and the word says which
// (Full also takes weight, LevelCell). The question the matrix answers first is
// "who can open People at all". Four steps of the ink read as two in light
// theme, where --hf-ink-2 and --hf-ink-3 are .64 and .60: View and None looked
// the same. Before that it was sky, iris and lilac, about forty tinted pills,
// three categorical hues for one "more access" scale (and before that, sage and
// amber, which read as "done" and "needs attention").
const ORG_ACCESS_LEVELS: { id: PermLevel; label: string; ink: string }[] = [
  { id: 'none', label: 'None', ink: 'var(--hf-ink-3)' },
  { id: 'view', label: 'View', ink: 'var(--hf-ink)' },
  { id: 'edit', label: 'Edit', ink: 'var(--hf-ink)' },
  { id: 'full', label: 'Full', ink: 'var(--hf-ink)' },
];

// A seeded role's colour comes from roleTone(), the map every other role badge
// reads; its roles.tone column still holds the old seed (owner = rose, the colour
// for "wrong"). A custom role isn't in ROLE_META, so its own column decides.
const orgRoleTone = (role: { id: string; tone: string }): ToneName => {
  const t = ROLE_META[role.id] ? roleTone(role.id) : role.tone;
  return Object.hasOwn(TONE, t) ? (t as ToneName) : 'neutral';
};
// Colours a new custom role may take. Categorical only: sage, amber and rose mean
// success, warning and wrong everywhere else in the app, and accent means
// selection and brand.
const ORG_ROLE_PALETTE: ToneName[] = ['iris', 'lilac', 'sky', 'coral', 'neutral'];
/** Every role under `id`, each once. Nothing in the database refuses a parent
 *  cycle, and walking one recursed until the stack overflowed: the Access tab
 *  crashed whenever a role in it was selected. */
const descendantsOf = (roles: Role[], id: string): string[] => {
  const out = new Set<string>();
  const walk = (p: string) =>
    roles.forEach((r) => {
      if (r.parent_id !== p || out.has(r.id)) return;
      out.add(r.id);
      walk(r.id);
    });
  walk(id);
  return [...out];
};

// ── helpers ───────────────────────────────────────────────────────────────────
const orgMinsOf = hhmmToMin;
const orgPad2 = (n: number): string => String(n).padStart(2, '0');
const byDate = (hs: Holiday[]) => [...hs].sort((a, b) => a.date.localeCompare(b.date));

// ── saving ────────────────────────────────────────────────────────────────────
// Every change on this screen saves itself, so every one of them reports back:
// a receipt that names what changed and offers Undo, or the failure, which
// stays until dismissed. Success used to be silent and failure a green pill
// with a check (ORG-02, ORG-09). Undo, not a confirm on every chip: the working
// week and the deduction toggle change hours and pay for everyone, and the way
// back from a mis-tap has to be one click, not a memory of what it said before.
type SaveNote = {
  /** What was changed, where the save can't name it itself. */
  what?: string;
  /** The receipt, in place of "{what} saved." */
  message?: string;
  /** This write IS an undo: its receipt offers no second one. */
  undo?: boolean;
};
// lib/write.ts: RLS filtering the row out is `refused`, not a success with no
// error — which is how a change could look saved and be gone on reload.
const REFUSED = 'your role can’t change this.';
const settle = (
  push: (t: ToastInput) => void,
  result: WriteResult,
  what: string,
  o: { message?: string; verb?: string; rollback: () => void; undo?: () => void },
): boolean => {
  if (!result.ok) {
    o.rollback(); // the optimistic change did not land
    push({
      tone: 'error',
      message: `${what} wasn’t ${o.verb ?? 'saved'}: ${result.reason === 'refused' ? REFUSED : result.error.message}`,
    });
    return false;
  }
  push({
    tone: 'success',
    message: o.message ?? `${what} saved.`,
    action: o.undo && { label: 'Undo', onClick: o.undo },
  });
  return true;
};

// An Undo whose write is no longer the last change to what it touched writes
// nothing, and says so.
const changedSince = (what: string): ToastInput => ({
  tone: 'info',
  message: `${what} changed again after this, so nothing was undone.`,
});

// What these settings would break, or undefined: a day no longer than its lunch
// break, core hours that end before they start, comp off recorded against an
// archived leave type. The controls refuse each as it is made; an Undo writes an older
// snapshot over whatever changed since, past all of them. Turn conversion off,
// archive its type, undo the switch, and month-end credited the archived type.
const orgFault = (org: OrgSettings, types: LeaveType[]): string | undefined => {
  if (orgMinsOf(org.day_end) - orgMinsOf(org.day_start) <= org.lunch_mins)
    return 'the working day would be no longer than its lunch break.';
  if (orgMinsOf(org.core_end) - orgMinsOf(org.core_start) <= 0)
    return 'core hours would end before they start.';
  const ot = org.policy.overtime;
  const target = ot.enabled ? types.find((t) => t.id === ot.targetType) : undefined;
  if (target?.archived)
    return `comp off would be recorded against ${target.label}, which is archived.`;
};
/** The Undo is refused for what it would break, not for what is broken already
 *  (old data): that can still be undone around. */
const undoFault = (
  now: OrgSettings,
  types: LeaveType[],
  then: OrgSettings,
  typesThen: LeaveType[],
): string | undefined => {
  const fault = orgFault(then, typesThen);
  return fault === orgFault(now, types) ? undefined : fault;
};

// Names what an org_settings write changed, for its receipt: "Lunch break saved."
const ORG_FIELD_LABEL: Partial<Record<keyof OrgSettings, string>> = {
  workdays: 'Working week',
  day_start: 'Standard day',
  day_end: 'Standard day',
  lunch_mins: 'Lunch break',
  timezone: 'Timezone',
  tz_iana: 'Timezone',
  week_start: 'Week start',
  date_format: 'Date format',
  core_start: 'Core hours',
  core_end: 'Core hours',
};
const POLICY_LABEL: Record<keyof Policy, string> = {
  approval: 'Approval settings',
  approvalLevels: 'Approval settings',
  overtime: 'Overtime conversion',
  leaveClash: 'Working on a leave day',
  employment: 'Employment standards',
  payroll: 'Payroll',
  calendar: 'Calendar settings',
  capacity: 'Capacity visibility',
  finance: 'Target utilisation',
  escalation: 'Escalation',
  editDay: 'Edit day',
};
const describeChange = (delta: Partial<OrgSettings>, before: Partial<OrgSettings>): string => {
  const names = new Set<string>();
  for (const k of Object.keys(delta) as (keyof OrgSettings)[]) {
    if (k !== 'policy') names.add(ORG_FIELD_LABEL[k] ?? 'Settings');
    // The policy is one jsonb column written whole: name the parts that moved.
    else
      for (const part of Object.keys(POLICY_LABEL) as (keyof Policy)[])
        if (JSON.stringify(delta.policy?.[part]) !== JSON.stringify(before.policy?.[part]))
          names.add(POLICY_LABEL[part]);
  }
  const [first, ...rest] = names;
  return !first ? 'Settings' : rest.length ? `${first} and ${rest.length} more` : first;
};

// An add form's submit. The form cleared or closed as it sent, so a write the
// database refused took the draft with it (ORG-13). `submit` resolves to
// whether the write landed, and the form resets only then; `busy` keeps a
// second press from adding the same thing twice.
const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const submit = async (save: () => Promise<boolean>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    const ok = await save();
    setBusy(false);
    return ok;
  };
  return [busy, submit] as const;
};

// A <fieldset disabled> with none of its own chrome: the native way to disable
// every button, input and picker inside it for a read-only viewer.
const bareFieldset: CSSProperties = { border: 0, padding: 0, margin: 0, minWidth: 0 };

// ── layout ────────────────────────────────────────────────────────────────────
// Two columns that fold to one when there is no room for both. Every section
// was a fixed `1.25fr 1fr`-style grid, so at phone width the cards overlapped
// and the helper text wrapped a word per line (ORG-17). Wrapping flex, no
// breakpoint: a column keeps its share while both fit and takes the row when
// they don't.
const splitRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 'var(--hf-space-4)',
};
const stack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-4)',
  minWidth: 0,
};
const splitCol = (grow: number, basis = 320): CSSProperties => ({
  ...stack,
  flex: `${grow} 1 ${basis}px`,
});
// A card header's aside, and the explanatory line under a card's controls.
// Mono is for a figure ("5 days · 40h / wk"); an aside in words takes the
// text face, proseMeta: "defaults · a hire may deviate" was set in mono, as
// if it were data (critique 2026-10-06, minor).
const proseMeta: CSSProperties = { fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' };
const metaStyle: CSSProperties = { ...proseMeta, fontFamily: 'var(--hf-font-mono)' };
const noteStyle: CSSProperties = {
  fontSize: 'var(--hf-text-xs)',
  color: 'var(--hf-ink-3)',
  lineHeight: 'var(--hf-leading-body)',
};
// Text that stays on its line and is cut with an ellipsis when it can't fit.
const oneLine: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Touch keys on the pointer, not the width (UI-CONVENTIONS §8): a tablet has a
// finger, a narrow desktop window has a mouse. 44px is WCAG 2.5.5's and Apple's
// target; under a mouse nothing here changes size.
const TOUCH = 44;
const useCoarse = () => useMediaQuery('(pointer: coarse)');
// A disclosure's <summary>: its one line of text was a ~18px target.
const tapSummary = (coarse: boolean): CSSProperties =>
  coarse ? { minHeight: TOUCH, paddingBlock: 'var(--hf-space-3)', boxSizing: 'border-box' } : {};
const summaryStyle = (coarse: boolean): CSSProperties => ({
  cursor: 'pointer',
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-2)',
  ...tapSummary(coarse),
});
// Archive and Unarchive: a row's one action. When the row wraps (a phone, or
// the narrow column at 1024) it keeps to the row's end edge, under the reason
// it is blocked, not alone at the start of a line below the name. Under a
// finger it grows to 44px for real rather than by an invisible area, so what a
// thumb aims at is what it hits.
const rowAction = (coarse: boolean): CSSProperties => ({
  marginLeft: 'auto',
  ...(coarse && { minHeight: TOUCH }),
});

// ── add forms ─────────────────────────────────────────────────────────────────
// An add form waits behind a button at the top of its list (UI-CONVENTIONS §6,
// critique 2026-10-05 #4). Holidays, leave types, departments and event types
// each kept a "New X" card open beside the list: the list gave up a third of
// the width to it, and at rest its disabled Add was the loudest thing on the
// tab. The form opens under the button, in a flat panel in the list's card;
// DisclosedForm takes focus into it, and back to the button when it closes,
// by Cancel or by a save, as the HR record's add forms do.
// The cards have no title (the section's heading is theirs), so this row is
// their header: the list's count and the button, at its end.
const listHead: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 'var(--hf-space-2) var(--hf-space-4)',
  marginBottom: 'var(--hf-space-3)',
};
// A list card's header: how many there are, beside "+ Add X", as Roles says
// "9 roles". The header held the button and nothing else, a ~56px band
// (critique 2026-10-06 #4). Nothing to say and nothing to add draws nothing.
const ListHead = ({ count, children }: { count?: string; children?: ReactNode }) =>
  count || children ? (
    <div style={listHead}>
      {count && <span style={metaStyle}>{count}</span>}
      {children}
    </div>
  ) : null;
/** "4 types", "1 department · 1 archived": the rows in use, then any archived. */
const listCount = (rows: { archived?: boolean }[], noun: string): string | undefined => {
  if (!rows.length) return undefined; // the empty state says so
  const archived = rows.filter((r) => r.archived).length;
  const n = rows.length - archived;
  return `${n} ${n === 1 ? noun : `${noun}s`}${archived ? ` · ${archived} archived` : ''}`;
};
const addPanel: CSSProperties = {
  ...stack,
  padding: 'var(--hf-space-4)',
  borderRadius: 'var(--hf-radius-md)',
  marginBottom: 'var(--hf-space-3)',
};
const fieldRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 'var(--hf-space-3) var(--hf-space-4)',
};
// What opens the form. `id` goes round it, for DisclosedForm's `opener`; a
// small button, so it takes a finger's 44px under a coarse pointer.
const AddOpener = ({
  id,
  disabled,
  onClick,
  children,
}: {
  id: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <span id={id}>
    <Button
      variant="secondary"
      size="sm"
      icon="plus"
      className="hf-hit"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  </span>
);
// The form's submit, its Cancel, and why the submit is off when it is (ORG-13).
const AddActions = ({
  children,
  busy,
  reason,
  blocked,
  onCancel,
}: {
  children: ReactNode;
  busy: boolean;
  /** Said under the buttons while the submit is off for it. */
  reason?: string;
  /** Off with no line of its own: the field already says why (a name in use). */
  blocked?: boolean;
  onCancel: () => void;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-2)' }}>
      <Button
        variant="primary"
        type="submit"
        className="hf-hit"
        disabled={!!reason || blocked}
        loading={busy}
      >
        {children}
      </Button>
      <Button variant="ghost" className="hf-hit" onClick={onCancel}>
        Cancel
      </Button>
    </div>
    {reason && <div style={noteStyle}>{reason}</div>}
  </div>
);

// The platform's time input. This was a bespoke picker: a button that opened 26
// option buttons in a panel portalled to <body>, with no arrow keys, no focus
// ring, and Tab leaving the panel open behind you (ORG-V1, ORG-07). The native
// control brings typing, arrow keys and the phone's own picker. A cleared or
// half-typed field reports '', which is not a time to save.
//
// A start and an end are one setting, saved together, on leaving a field or on
// Enter, and only while they make a span. Each field used to save as it changed:
// an end before its start was saved under its own error, as a negative working
// day every hours calculation then read, and a pause mid-typing saved the time
// on its way (01:30, typing 10:30). Until the pair is valid it stays a draft
// under its error. `floor`: the span must be longer than this, in minutes.
const TimeRange = ({
  labels: [fromLabel, toLabel],
  start,
  end,
  floor = 0,
  floorError,
  onSave,
}: {
  labels: [string, string];
  start: string;
  end: string;
  floor?: number;
  floorError?: string;
  onSave: (start: string, end: string) => void;
}) => {
  const [draft, setDraft] = useState<{ start: string; end: string } | null>(null);
  // On a phone each field takes the row: at 16px (iOS's floor) two 8.5rem
  // fields side by side cut "09:30 AM" off at its edge.
  const phone = useBelow('md');
  const v = draft ?? { start, end };
  const span = orgMinsOf(v.end) - orgMinsOf(v.start);
  const error = span <= 0 ? 'Ends before it starts.' : span <= floor ? floorError : undefined;
  const commit = () => {
    if (!draft || error) return;
    setDraft(null);
    if (draft.start !== start || draft.end !== end) onSave(draft.start, draft.end);
  };
  const field = (label: string, k: 'start' | 'end') => (
    <Field
      label={label}
      error={k === 'end' ? error : undefined}
      style={{ width: phone ? '100%' : '8.5rem' }}
    >
      <TextInput
        type="time"
        step={300}
        mono
        dense
        value={v[k]}
        onChange={(t) => t && setDraft({ ...v, [k]: t })}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
      />
    </Field>
  );
  return (
    <>
      {field(fromLabel, 'start')}
      {field(toLabel, 'end')}
    </>
  );
};

// Every zone the platform knows, labelled with the offset in force today. There
// were five literal options whose labels stated the winter offset all year, and
// a studio in Singapore or Berlin could not set its zone at all (ORG-14).
//
// Labelling them takes ~2,000 Intl formatters (166–734 ms measured), which ran
// on the first render of the default tab, for every viewer. They are built the
// first time the picker is focused or clicked, once per session; until then it
// holds the stored zone alone. The stored zone leads the list either way, so the
// option a click opens on is the same before and after the list fills.
let zoneOptions: SelectOption[] | null = null;
const timezoneOptions = (current: string): SelectOption[] => {
  zoneOptions ??= (Intl.supportedValuesOf?.('timeZone') ?? []).map((z) => ({
    value: z,
    label: tzLabel(z),
  }));
  // The stored zone is always offered: engines list an alias under one of its
  // names only (Asia/Calcutta for Asia/Kolkata), and both are valid to Postgres.
  return [
    { value: current, label: tzLabel(current) },
    ...zoneOptions.filter((o) => o.value !== current),
  ];
};
const TimezoneSelect = ({
  value,
  onChange,
  ...control
}: {
  value: string;
  onChange: (v: string) => void;
  /** A Field's control props, passed through so its hint describes the list. */
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}) => {
  const [wanted, setWanted] = useState(false);
  const want = () => setWanted(true);
  return (
    <div onFocusCapture={want} onClickCapture={want}>
      <Select
        {...control}
        value={value}
        options={wanted ? timezoneOptions(value) : [{ value, label: tzLabel(value) }]}
        onChange={onChange}
      />
    </div>
  );
};

const OrgRow = ({
  title,
  desc,
  children,
  last,
}: {
  title: string;
  desc?: ReactNode;
  children: ReactNode;
  last?: boolean;
}) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 'var(--hf-space-2) var(--hf-space-4)',
      padding: 'var(--hf-space-3) 0',
      borderBottom: last ? 'none' : '1px solid var(--hf-rule)',
    }}
  >
    <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>{title}</div>
      {desc && <div style={{ ...noteStyle, marginTop: 'var(--hf-space-1)' }}>{desc}</div>}
    </div>
    <div style={{ flexShrink: 0, maxWidth: '100%' }}>{children}</div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// WORK TIME · EDIT DAY
// ═════════════════════════════════════════════════════════════════════════════
// The rollout of the one whole-day dialog (owner, 2026-11-03): HR and managers
// first, then everyone; the old correction dialogs stay until then. It only
// decides which dialog shows: the server's rules are the same either way.
const EDIT_DAY_OPTIONS: { value: EditDayRollout; label: string }[] = [
  { value: 'off', label: 'Off — the old dialogs' },
  { value: 'hr', label: 'HR and managers' },
  { value: 'everyone', label: 'Everyone' },
];
const OrgEditDayCard = ({ policy, patch }: { policy: Policy; patch: (p: Policy) => void }) => (
  <PCard level={3} label="Edit day">
    <OrgRow
      title="Who uses Edit day"
      desc="One dialog for a whole day: check-ins and check-outs, missed time, credited hours and project time. HR and managers change a day directly; with Everyone, people ask for a change to their own day in the same dialog"
      last
    >
      <Select
        ariaLabel="Who uses Edit day"
        value={policy.editDay?.rollout ?? 'off'}
        options={EDIT_DAY_OPTIONS}
        onChange={(v) => patch({ ...policy, editDay: { rollout: v as EditDayRollout } })}
      />
    </OrgRow>
  </PCard>
);

// ═════════════════════════════════════════════════════════════════════════════
// WORK TIME · OFFICE HOURS
// ═════════════════════════════════════════════════════════════════════════════
// The working day with the core window inside it. Its axis was pinned to
// 07:30–20:30 whatever the day was set to; it now runs an hour either side of
// whichever of the two windows reaches further.
const DayBand = ({ org }: { org: OrgSettings }) => {
  // Three marks below lg. On a phone five 12-hour times ("08:30 AM") overran a
  // 320px card and broke onto two lines each; from md the band sits in a
  // half-width card, 284px at 768, where they ran together 3px apart.
  const ticks = useBelow('lg') ? [0, 2, 4] : [0, 1, 2, 3, 4];
  const day = [orgMinsOf(org.day_start), orgMinsOf(org.day_end)];
  const core = [orgMinsOf(org.core_start), orgMinsOf(org.core_end)];
  const from = Math.max(0, Math.min(day[0], core[0]) - 60);
  const to = Math.min(24 * 60, Math.max(day[1], core[1]) + 60);
  const at = (min: number) => ((min - from) / Math.max(1, to - from)) * 100;
  const seg = ([a, b]: number[], tone: ToneName, inset: boolean): CSSProperties => ({
    position: 'absolute',
    top: inset ? 'var(--hf-space-2)' : 0,
    bottom: inset ? 'var(--hf-space-2)' : 0,
    left: `${at(a)}%`,
    width: `${Math.max(0, at(b) - at(a))}%`,
    // The core window sits on the day's wash, so it takes the stronger tint.
    background: inset ? TONE[tone].bd : TONE[tone].bg,
    borderLeft: `1px solid ${TONE[tone].bd}`,
    borderRight: `1px solid ${TONE[tone].bd}`,
    borderRadius: inset ? 'var(--hf-radius-sm)' : 0,
  });
  return (
    <div
      role="img"
      aria-label={`Core hours ${fmtTimeOfDay(core[0])} to ${fmtTimeOfDay(core[1])}, inside a working day of ${fmtTimeOfDay(day[0])} to ${fmtTimeOfDay(day[1])}`}
      style={{ marginTop: 'var(--hf-space-4)' }}
    >
      <div
        style={{
          position: 'relative',
          height: 'var(--hf-space-6)',
          borderRadius: 'var(--hf-radius-md)',
          background: 'var(--hf-surface)',
          border: '1px solid var(--hf-rule)',
          overflow: 'hidden',
        }}
      >
        <div style={seg(day, 'accent', false)} />
        <div style={seg(core, 'lilac', true)} />
      </div>
      <div
        style={{
          ...metaStyle,
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 'var(--hf-space-1)',
        }}
      >
        {ticks.map((i) => (
          <span key={i}>{fmtTimeOfDay(Math.round(from + ((to - from) * i) / 4))}</span>
        ))}
      </div>
    </div>
  );
};

export const OrgHoursTab = ({
  org,
  patch,
}: {
  org: OrgSettings;
  patch: (d: Partial<OrgSettings>) => void;
}) => {
  const toggleDay = (d: string) => patch({ workdays: { ...org.workdays, [d]: !org.workdays[d] } });
  // Same derivation every other screen and the server use — see lib/orgHours.
  const orgHours = orgHoursFrom(org);
  const perDay = fmtDuration(orgHours.perDay * 3600, 'perDay');
  const perWeek = `${fmtDuration(orgHours.perWeek * 3600, 'decimal')}h`;
  const dayMins = orgMinsOf(org.day_end) - orgMinsOf(org.day_start);
  // A lunch break as long as the day leaves no working time: the stepper stops
  // a step short of the span, as the span stops short of the lunch break.
  const lunchMax = Math.max(0, Math.min(120, Math.ceil(dayMins / 15) * 15 - 15));
  const coreMins = orgMinsOf(org.core_end) - orgMinsOf(org.core_start);
  // Read once: the date-format example only has to be a real date.
  const [now] = useState(() => Date.now());
  const phone = useBelow('md');

  return (
    <div style={splitRow}>
      {/* Each column sets one thing: the week (which days, the zone they fall
          in, where the week starts, how a date reads) and the day (its hours,
          lunch, the core window). Standard day sat over Working week, away
          from the core window drawn against it, and the left column ended
          about 200px above the right. */}
      <div style={splitCol(5)}>
        <PCard
          level={3}
          label="Working week"
          right={
            <span style={metaStyle}>
              {orgHours.workdayCount} days · {perWeek} / wk
            </span>
          }
        >
          <div
            role="group"
            aria-label="Working days"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              // At 320 an 8px gap left each day 29px, and "Wed" ran past its
              // border; 4px gives the seven days the width instead.
              gap: phone ? 'var(--hf-space-1)' : 'var(--hf-space-2)',
            }}
          >
            {ORG_DAYS.map((d) => {
              const on = !!org.workdays[d];
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleDay(d)}
                  style={{
                    padding: 'var(--hf-space-3) 0',
                    borderRadius: 'var(--hf-radius-md)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 'var(--hf-text-sm)',
                    fontWeight: 'var(--hf-weight-medium)',
                    transition: 'background var(--hf-dur-hover), border-color var(--hf-dur-hover)',
                    background: on ? 'var(--hf-accent-wash)' : 'var(--hf-surface)',
                    // Off, the border is the chip's only edge.
                    border: `1px solid ${on ? 'var(--hf-accent-line)' : 'var(--hf-rule-control)'}`,
                    color: on ? 'var(--hf-accent-text)' : 'var(--hf-ink-3)',
                  }}
                >
                  <span style={{ display: 'block' }}>{d}</span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--hf-text-2xs)',
                      fontFamily: 'var(--hf-font-mono)',
                      marginTop: 'var(--hf-space-1)',
                    }}
                  >
                    {on ? 'on' : 'off'}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
            Non-working days are excluded from timesheet targets, leave-day counts and capacity
            forecasts.
          </div>
        </PCard>

        <PCard level={3} label="Locale & timezone">
          <div style={stack}>
            <Field
              label="Timezone"
              hint="Decides which calendar day a check-in, a leave date and a holiday fall on."
            >
              {(c) => (
                <TimezoneSelect
                  {...c}
                  value={org.tz_iana}
                  // Both columns. `tz_iana` is the one SQL org_today() reads to
                  // stamp attendance.date and gate leave dates; `timezone` is its
                  // display label, kept in step until the two become one column.
                  onChange={(v) => patch({ tz_iana: v, timezone: tzLabel(v) })}
                />
              )}
            </Field>
            <div style={splitRow}>
              <Field label="Week starts on" style={{ flex: '1 1 8rem' }}>
                {(c) => (
                  <Select
                    {...c}
                    value={org.week_start}
                    onChange={(v) => patch({ week_start: v })}
                    options={['Mon', 'Sun', 'Sat'].map((d) => ({ value: d, label: d }))}
                  />
                )}
              </Field>
              {/* date_format has a reader now: fmtDate, fed by useOrgDates(). The
                  hint shows the choice at work instead of "not applied yet". */}
              <Field
                label="Date format"
                hint={`Today reads ${fmtDate(orgDateKey(now, org.tz_iana), org.date_format)}.`}
                style={{ flex: '1 1 8rem' }}
              >
                {(c) => (
                  <Select
                    {...c}
                    value={org.date_format}
                    onChange={(v) => patch({ date_format: v })}
                    options={DATE_FORMATS.map((f) => ({ value: f, label: f }))}
                  />
                )}
              </Field>
            </div>
          </div>
        </PCard>
      </div>

      <div style={splitCol(4)}>
        {/* What the day comes to, said once, as Working week says its total.
            It was an accent badge AND the first of three figures under the
            fields: "Span" repeated the two times above it, and "Per week" the
            Working week's own total. The contracted day's one shape (perDay,
            UI-CONVENTIONS Vocabulary); "net" is about the figure, so it is the
            words after it, not inside it ("8h 00m net / day"). */}
        <PCard
          level={3}
          label="Standard day"
          right={<span style={metaStyle}>{perDay} after breaks</span>}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--hf-space-4)',
              flexWrap: 'wrap',
            }}
          >
            <TimeRange
              labels={['Starts', 'Ends']}
              start={org.day_start}
              end={org.day_end}
              floor={org.lunch_mins}
              floorError={`Must be longer than the ${org.lunch_mins} min lunch break.`}
              onSave={(day_start, day_end) => patch({ day_start, day_end })}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--hf-space-2)',
                // Stacked on a phone it lines up under Starts and Ends; pushed
                // right, it sat alone at the far edge of its row.
                marginLeft: phone ? 0 : 'auto',
              }}
            >
              <Eyebrow>Lunch break</Eyebrow>
              <Stepper
                label="Lunch break"
                unit="min"
                value={org.lunch_mins}
                onChange={(v) => patch({ lunch_mins: v })}
                min={0}
                max={lunchMax}
                step={15}
              />
            </div>
          </div>
        </PCard>

        {/* The window's length in the card's meta, as Standard day's net:
            a lilac badge made a setting's echo the loudest thing on it. */}
        <PCard
          level={3}
          label="Core collaboration hours"
          right={<span style={metaStyle}>{fmtDuration(coreMins * 60, 'hm')} window</span>}
        >
          <div style={{ ...noteStyle, marginBottom: 'var(--hf-space-3)' }}>
            The window where everyone is expected online for meetings & reviews. Outside it, hours
            are flexible. Recorded here as the standard; the OS does not check anyone against it.
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--hf-space-4)',
              flexWrap: 'wrap',
            }}
          >
            <TimeRange
              labels={['Core from', 'Core to']}
              start={org.core_start}
              end={org.core_end}
              onSave={(core_start, core_end) => patch({ core_start, core_end })}
            />
          </div>
          <DayBand org={org} />
        </PCard>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// WORK TIME · HOLIDAYS
// ═════════════════════════════════════════════════════════════════════════════
// The list is the view (ORG-10). This was twelve mini-months and nothing else:
// 365 day buttons standing for seven holidays, a holiday's name readable only by
// clicking its cell, and 358 Tab stops between the keyboard and the Add form.
// The year is still drawn, below, as twelve MonthGrids: one Tab stop each, arrow
// keys inside, and a day's holidays read out with its date.
const HOLIDAY_KIND_IDS = Object.keys(ORG_HOLIDAY_KINDS) as HolidayKind[];
const KIND_OPTIONS = HOLIDAY_KIND_IDS.map((k) => ({ value: k, label: ORG_HOLIDAY_KINDS[k].label }));
const BLANK_HOLIDAY = { date: '', name: '', kind: 'public' as HolidayKind, repeat: false };

export const OrgHolidaysTab = ({
  holidays,
  weekStartsOn,
  workdays,
  tz,
  editable,
  onAdd,
  onRemove,
  onSetKind,
}: {
  holidays: Holiday[];
  /** Week start and zone as this screen holds them. They are edited a section
   *  above, on the same tab: read from the useOrgDates() cache, the grids kept
   *  the old week start until the tab was left and reopened. */
  weekStartsOn: number;
  /** The working week as this screen holds it, for the same reason: a day off
   *  is hatched. */
  workdays: Workdays;
  tz: string;
  /** The year picker, the list and the calendar stay live either way. */
  editable: boolean;
  /** Resolves to whether the holiday was added. */
  onAdd: (h: {
    date: string;
    name: string;
    kind: HolidayKind;
    repeat: boolean;
  }) => Promise<boolean>;
  onRemove: (id: string) => void;
  onSetKind: (h: Holiday, kind: HolidayKind) => void;
}) => {
  const [now] = useState(() => Date.now());
  const today = orgDateKey(now, tz);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [draft, setDraft] = useState(BLANK_HOLIDAY);
  const [adding, submit] = useSubmit();
  // Add holiday, and how it was opened: from a day in the calendar the date is
  // filled already, so the name takes the focus.
  const [open, setOpen] = useState<false | 'button' | 'day'>(false);
  const opener = useId();
  const phone = useBelow('md');
  const coarse = useCoarse();
  const formRef = useRef<HTMLFormElement>(null);
  const close = () => {
    setOpen(false);
    setDraft(BLANK_HOLIDAY);
  };
  const pickDay = useCallback(
    (date: string) => {
      setDraft((d) => ({ ...d, date }));
      // Opening it moves the focus there, which brings it into view.
      if (!open) return setOpen('day');
      // On a phone the open form is a screen or more above the months: filled
      // out of sight, the tap seemed to do nothing.
      if (phone) formRef.current?.scrollIntoView?.({ block: 'start' });
    },
    [open, phone],
  );

  const list = useMemo(
    () => byDate(holidays.filter((h) => h.date.startsWith(`${year}-`))),
    [holidays, year],
  );

  const name = draft.name.trim();
  const missing = [!draft.date && 'a date', !name && 'a name'].filter(Boolean);
  const add = async () => {
    if (missing.length || !(await submit(() => onAdd({ ...draft, name })))) return;
    // Show the year it went into: added for another year, it appeared nowhere.
    setYear(Number(draft.date.slice(0, 4)));
    close();
  };

  const counts = HOLIDAY_KIND_IDS.map((k) => ({ k, n: list.filter((h) => h.kind === k).length }));
  const summary = [
    `${list.length} ${list.length === 1 ? 'holiday' : 'holidays'}`,
    ...counts.filter((c) => c.n).map((c) => `${c.n} ${ORG_HOLIDAY_KINDS[c.k].label.toLowerCase()}`),
  ].join(' · ');

  // Every day draws a mark now, so the twelve months are ~1,000 nodes: kept
  // as they are while a holiday's name is typed, a keystroke at a time.
  const months = useMemo(() => {
    // One day can carry several holidays: there is no unique index on the date.
    const onDay = (key: string) => list.filter((h) => h.date === key);
    // A week with no mark gives its first day an empty line of marks: a week
    // holding a mark was a row taller than one without, so January's rows
    // changed height (critique 2026-10-06, minor). One day is enough, as a
    // week is as tall as its tallest day; on every unmarked day it was ~1,500
    // more nodes, and the year rendered twice as slowly.
    const marked = (key: string) => onDay(key).length > 0 || !isWorkday(key, workdays);
    const unmarkedWeekStarts = (key: string) => {
      const [y, m, d] = key.split('-').map(Number);
      const lead = (new Date(y, m - 1, 1).getDay() - weekStartsOn + 7) % 7;
      const start = d - ((lead + d - 1) % 7); // the week's first column, maybe last month
      if (Math.max(1, start) !== d) return false;
      const end = Math.min(start + 6, new Date(y, m, 0).getDate());
      for (let n = d; n <= end; n++) if (marked(`${key.slice(0, 8)}${orgPad2(n)}`)) return false;
      return true;
    };
    return (
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 15rem), 1fr))',
            gap: 'var(--hf-space-4)',
          }}
        >
          {MONTHS.map((month, m) => (
            <div
              key={month}
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}
            >
              <Eyebrow as="h4">{month}</Eyebrow>
              <MonthGrid
                compact
                year={year}
                month={m}
                weekStartsOn={weekStartsOn}
                today={today}
                label={`${month} ${year}`}
                selectedKey={draft.date || undefined}
                onSelectDay={editable ? pickDay : undefined}
                // The shared day marks (UI-CONVENTIONS, Day marks): a square in
                // the kind's tone, dashed for an optional holiday, which was a
                // filled square here and so read like every other kind. A day
                // off is hatched, as on the Workspace calendar: Sat and Sun were
                // off and drawn like any working day.
                // ponytail: the hatch is an item, so a day off holding three
                // holidays shows two and "+1"; share the first item's slot, as
                // calendar.tsx does, if a weekend ever holds three.
                renderDay={(key) => {
                  const marks = onDay(key).map((h) => (
                    <span key={h.id} title={h.name}>
                      <DayMark kind={h.kind} name={h.name} srOnly />
                    </span>
                  ));
                  if (!isWorkday(key, workdays))
                    marks.unshift(<DayMark key="off" kind="off" srOnly />);
                  // A line of the marks' height: text, so no box to size.
                  if (!marks.length && unmarkedWeekStarts(key))
                    marks.push(
                      <span key="none" aria-hidden="true">
                        {'\u00a0'}
                      </span>,
                    );
                  return marks;
                }}
              />
            </div>
          ))}
        </div>
        {editable && (
          <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
            Choosing a day here opens Add holiday on that date.
          </div>
        )}
        {/* The calendar's foot, as on every calendar: only the kinds this year
          holds. It sat in the card's header and keyed Regional in a year with
          none (critique 2026-10-06 #2). */}
        <DayMarkKey holidays={list} off={ORG_DAYS.some((d) => workdays[d] === false)} />
      </>
    );
  }, [list, year, weekStartsOn, today, draft.date, editable, pickDay, workdays]);

  return (
    <div style={stack}>
      {/* No card title: it said "Holidays · 2026" between the section's
          "Holidays" and the year stepper's "2026". */}
      <PCard level={3}>
        <div style={{ ...listHead, justifyContent: 'flex-start' }}>
          {/* Six year chips in a row pushed the page sideways at 320px, and a
              holiday outside those six could be added but never seen. */}
          <PeriodStepper
            label={String(year)}
            unit="year"
            labelWidth="3rem"
            onPrev={() => setYear(year - 1)}
            onNext={() => setYear(year + 1)}
            onReset={year === thisYear ? undefined : () => setYear(thisYear)}
            resetLabel="This year"
          />
          <span style={{ ...metaStyle, marginLeft: 'auto' }}>{summary}</span>
          {editable && !open && (
            <AddOpener id={opener} onClick={() => setOpen('button')}>
              Add holiday
            </AddOpener>
          )}
        </div>
        {editable && open && (
          <DisclosedForm
            opener={opener}
            first={open === 'day' ? '[name="holiday-name"]' : undefined}
          >
            <form
              ref={formRef}
              aria-label="Add holiday"
              className="hf-panel"
              onSubmit={(e) => {
                e.preventDefault();
                void add();
              }}
              style={{ ...addPanel, scrollMarginTop: 'var(--hf-space-4)' }}
            >
              <div style={fieldRow}>
                {/* The native box reads in the browser's order (mm/dd/yyyy)
                    under "Today reads 05-10-2026": DateField says the day back
                    in the org's format (UI-CONVENTIONS §2, Entering a date).
                    ponytail: the echo reads useOrgDates(), cached when the form
                    opens, so a Date format changed above while it is open shows
                    on its next opening. */}
                <DateField
                  label="Date"
                  required
                  value={draft.date}
                  onChange={(date) => setDraft({ ...draft, date })}
                  style={{ flex: '1 1 11rem' }}
                />
                <Field label="Name" required style={{ flex: '2 1 14rem' }}>
                  <TextInput
                    name="holiday-name"
                    value={draft.name}
                    onChange={(v) => setDraft({ ...draft, name: v })}
                    placeholder="e.g. Regional festival"
                  />
                </Field>
              </div>
              <div style={{ ...fieldRow, alignItems: 'flex-end' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--hf-space-2)',
                    // A phone's chips share the row (`full`), so the group takes it.
                    ...(phone && { width: '100%' }),
                  }}
                >
                  <Eyebrow>Type</Eyebrow>
                  <div>
                    <SegmentedControl
                      label="Type"
                      value={draft.kind}
                      options={KIND_OPTIONS}
                      onChange={(kind) => setDraft({ ...draft, kind })}
                      full={phone}
                    />
                  </div>
                </div>
                <Checkbox
                  label="Repeat yearly (next 5 years)"
                  checked={draft.repeat}
                  onChange={(repeat) => setDraft({ ...draft, repeat })}
                />
              </div>
              <AddActions
                busy={adding}
                reason={
                  missing.length
                    ? `Needs ${missing.join(' and ')} before it can be added.`
                    : undefined
                }
                onCancel={close}
              >
                Add {draft.repeat ? 'every year' : `to ${draft.date.slice(0, 4) || 'calendar'}`}
              </AddActions>
            </form>
          </DisclosedForm>
        )}
        {list.length === 0 ? (
          <EmptyState title={`No holidays in ${year}`}>
            {editable ? 'Add one with Add holiday.' : 'None have been added for this year.'}
          </EmptyState>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {list.map((h) => (
              <li
                key={h.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2) var(--hf-space-3)',
                  padding: 'var(--hf-space-2) 0',
                  borderTop: '1px solid var(--hf-rule)',
                }}
              >
                <Mono style={{ width: '6.5rem', flexShrink: 0, color: 'var(--hf-ink-2)' }}>
                  {fmtDayKey(h.date, { weekday: true })}
                </Mono>
                <span
                  style={{
                    flex: '1 1 8rem',
                    minWidth: 0,
                    fontSize: 'var(--hf-text-md)',
                    color: 'var(--hf-ink)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {h.name}
                </span>
                {/* The type was a badge you clicked to cycle through four
                    kinds (ORG-21). A viewer who can't change it reads it. */}
                {editable ? (
                  <>
                    <div style={{ width: '8.5rem' }}>
                      <Select
                        ariaLabel={`Type of ${h.name}`}
                        value={h.kind}
                        options={KIND_OPTIONS}
                        align="right"
                        onChange={(k) => onSetKind(h, k as HolidayKind)}
                      />
                    </div>
                    <IconButton
                      icon="x"
                      label={`Remove ${h.name}`}
                      onClick={() => onRemove(h.id)}
                    />
                  </>
                ) : (
                  <Badge tone={ORG_HOLIDAY_KINDS[h.kind].tone} dot>
                    {ORG_HOLIDAY_KINDS[h.kind].label}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
        {/* What a holiday does, said at rest: it lived inside the closed Add
            holiday form, so the tab no longer said it (critique 2026-10-06 #4). */}
        <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
          Every holiday is a day off for everyone, whatever its type. Hours worked on any holiday
          count as overtime, same as any other day, while overtime is on.
        </div>
      </PCard>

      <PCard level={3} label={`Calendar · ${year}`}>
        {/* On a phone the list above is the view, and the twelve months, each a
            screen tall, wait behind a disclosure: always open, they were
            3,800px of scrolling at 390px wide. */}
        {phone ? (
          <details>
            <summary style={summaryStyle(coarse)}>Month by month</summary>
            <div style={{ marginTop: 'var(--hf-space-3)' }}>{months}</div>
          </details>
        ) : (
          months
        )}
      </PCard>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// PEOPLE SETUP · EMPLOYMENT
// ═════════════════════════════════════════════════════════════════════════════
// Cards side by side while there is room, one column when there isn't (ORG-17).
const cardGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 26rem), 1fr))',
  gap: 'var(--hf-space-4)',
  alignItems: 'start',
};

// A text setting: the draft stays in the box and is saved on blur or Enter, and
// only when it changed. It used to write the whole policy per keystroke, so a
// flaky connection undid the typing one character at a time (ORG-V2). The
// caller keys it on the saved value, so a save — or a failed one rolled back —
// shows what is actually stored.
const TextSetting = ({
  label,
  value,
  onSave,
}: {
  label: string;
  value: string;
  onSave: (v: string) => void;
}) => {
  const [draft, setDraft] = useState(value);
  return (
    <TextInput
      aria-label={label}
      value={draft}
      onChange={setDraft}
      onBlur={() => {
        if (draft !== value) onSave(draft);
      }}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') ev.currentTarget.blur();
      }}
      spellCheck={false}
      mono
      dense
      // Wide enough for the whole address: the domain was cut off at 190px
      // (ORG-18), which is the part an admin needs to check.
      style={{ width: '18rem', maxWidth: '100%' }}
    />
  );
};

// R3 — the standards a hire is set up against, in one place instead of in
// somebody's memory. Every control here has a reader: the email format and the
// access map feed the invite modal, probation and the reminder lead time feed
// the date register, notice feeds notice-expiry dates. The one exception is
// labelled as such on screen.
export const OrgEmploymentTab = ({
  policy,
  patch,
}: {
  policy: Policy;
  patch: (p: Policy) => void;
}) => {
  const e = policy.employment;
  const setE = (delta: Partial<Employment>) => patch({ ...policy, employment: { ...e, ...delta } });
  const e2 = policy.payroll;
  const setPay = (delta: Partial<Payroll>) => patch({ ...policy, payroll: { ...e2, ...delta } });

  return (
    <div style={cardGrid}>
      <PCard
        level={3}
        label="Engagement standards"
        right={<span style={proseMeta}>defaults · a hire may deviate</span>}
      >
        <OrgRow title="Probation period" desc="Sets the probation-end date for a new hire">
          <Stepper
            label="Probation period"
            unit="mo"
            value={e.probation_months}
            onChange={(v) => setE({ probation_months: v })}
            min={0}
            max={12}
          />
        </OrgRow>
        <OrgRow title="Notice — employee" desc="Notice the employee gives">
          <Stepper
            label="Notice — employee"
            unit="days"
            value={e.notice_days_employee}
            onChange={(v) => setE({ notice_days_employee: v })}
            min={0}
            max={180}
          />
        </OrgRow>
        <OrgRow title="Notice — company" desc="Notice the company gives">
          <Stepper
            label="Notice — company"
            unit="days"
            value={e.notice_days_company}
            onChange={(v) => setE({ notice_days_company: v })}
            min={0}
            max={180}
          />
        </OrgRow>
        <OrgRow title="Reminder lead time" desc="How far ahead a dated event is flagged" last>
          <Stepper
            label="Reminder lead time"
            unit="days"
            value={e.reminder_lead_days}
            onChange={(v) => setE({ reminder_lead_days: v })}
            min={1}
            max={90}
          />
        </OrgRow>
      </PCard>

      <PCard
        level={3}
        label="Payroll"
        right={<span style={proseMeta}>before statutory deductions</span>}
      >
        <OrgRow
          title="Deduct for shortfall"
          desc="Where effective hours fall below required: shortfall / required x monthly gross"
        >
          {/* Spread: the toggle saved { enabled } alone, which wiped the waiver. */}
          <Toggle
            label="Deduct for shortfall"
            checked={e2.deduction.enabled}
            onChange={(v) => setPay({ deduction: { ...e2.deduction, enabled: v } })}
          />
        </OrgRow>
        <OrgRow
          title="Waive a small shortfall"
          desc="A month short by less than this deducts nothing. At or above it, HR chooses pay or leave per person in the payroll register."
          last
        >
          <Stepper
            label="Waive a small shortfall"
            unit="hrs"
            value={e2.deduction.waiveBelowHours}
            onChange={(v) => setPay({ deduction: { ...e2.deduction, waiveBelowHours: v } })}
            min={0}
            max={24}
          />
        </OrgRow>
      </PCard>

      <PCard level={3} label="Provisioning">
        <OrgRow
          title="Company email format"
          desc="Suggested when inviting someone. {first} and {last} are filled from their name."
        >
          <TextSetting
            key={e.email_format}
            label="Company email format"
            value={e.email_format}
            onSave={(v) => setE({ email_format: v })}
          />
        </OrgRow>
        <OrgRow
          title="Offer validity"
          desc="How long an issued offer stands. Recorded here as the standard; the OS does not track offers."
          last
        >
          <Stepper
            label="Offer validity"
            unit="days"
            value={e.offer_acceptance_days}
            onChange={(v) => setE({ offer_acceptance_days: v })}
            min={1}
            max={90}
          />
        </OrgRow>
      </PCard>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// LEAVE · POLICY
// ═════════════════════════════════════════════════════════════════════════════
// A leave type's colour is a hex stored on its row, so its mark is that hex:
// an edge in the colour and a wash of it behind.
const typeSwatch = (color: string): CSSProperties => ({
  flexShrink: 0,
  borderRadius: 'var(--hf-radius-sm)',
  background: `color-mix(in srgb, ${color} 20%, transparent)`,
  border: `1.5px solid ${color}`,
});

export const OrgPolicyTab = ({
  policy,
  leaveTypes,
  patch,
}: {
  policy: Policy;
  leaveTypes: LeaveType[];
  patch: (p: Policy) => void;
}) => {
  const p = policy;
  const setP = (delta: Partial<Policy>) => patch({ ...p, ...delta });
  const ot = p.overtime;
  const setOT = (delta: Partial<Overtime>) => setP({ overtime: { ...ot, ...delta } });
  const lc = p.leaveClash;
  const setLC = (delta: Partial<LeaveClash>) => setP({ leaveClash: { ...lc, ...delta } });
  // An archived type is no longer offered, nor one with no yearly standard (no
  // room, so comp off could never be credited, 20261022100000) — except the one
  // already chosen, so the picker still shows what the setting is.
  const targets = leaveTypes.filter(
    (t) => (!t.archived && t.days_per_year > 0) || t.id === ot.targetType,
  );
  const target = leaveTypes.find((t) => t.id === ot.targetType);
  const otTarget = target?.label ?? '—';
  // Leave types refuses to archive this type while conversion is on, but not
  // while it is off; turned back on, conversion credited the archived type
  // without a word. It stays off until the type is back in use: the picker that
  // could choose another is off with it.
  const targetArchived =
    target?.archived &&
    `Comp off is recorded against ${target.label}, which is archived. ${ot.enabled ? 'Choose a type in use under Comp off type.' : 'Unarchive it under Leave types to turn comp off on.'}`;
  // Blocked, the switch stays focusable and is described by the note, so a
  // keyboard user who reaches it hears why it won't turn on.
  const blocked = !ot.enabled && !!targetArchived;
  const whyId = useId();
  const unenforcedId = useId();

  return (
    <div style={stack}>
      <div style={cardGrid}>
        <PCard
          level={3}
          label="Overtime → comp off"
          right={
            <Toggle
              label="Overtime → comp off"
              checked={ot.enabled}
              disabled={blocked}
              focusableWhenDisabled
              describedBy={blocked ? whyId : undefined}
              onChange={(v) => setOT({ enabled: v })}
            />
          }
        >
          <div
            style={{
              ...noteStyle,
              paddingBottom: 'var(--hf-space-3)',
              borderBottom: '1px solid var(--hf-rule)',
            }}
          >
            {/* Owner, 2026-10-22: comp off is a count of its own and never changes a
                leave balance, "leaves used are leaves used"; a month credits only
                within its type's room (20261022100000), replacing 2026-10-19's
                giving back used days. 2026-10-16: from the month's surplus,
                effective over required hours. */}
            A month&apos;s overtime is its surplus: effective hours (clock plus credited) over its
            required hours. When the month is locked for payroll, the surplus counts as comp off in
            half days — every {ot.hoursPerLeave / 2}h is ½ day — up to the limits below. Comp off is
            counted on its own and never changes {otTarget}: days used stay used. A month credits
            only while {otTarget} has room under its yearly standard (the standard less the{' '}
            {otTarget} days left, less comp off already credited this year); overtime past that is
            dropped. Hours left over are dropped too, never carried into the next month. A type with
            no yearly standard has no room, so it isn&apos;t offered.
            {targetArchived && (
              <div id={whyId} style={{ marginTop: 'var(--hf-space-2)' }}>
                {targetArchived}
              </div>
            )}
          </div>
          {/* Off means disabled. It was an opacity + pointer-events wrapper, which
              looked off and still took every keystroke (ORG-07). */}
          <fieldset disabled={!ot.enabled} style={bareFieldset}>
            <OrgRow
              title="Hours per leave"
              desc="Overtime that earns one comp off day; half of it earns ½ day"
            >
              <Stepper
                label="Hours per leave"
                unit="hrs"
                value={ot.hoursPerLeave}
                onChange={(v) => setOT({ hoursPerLeave: v })}
                min={1}
                max={40}
              />
            </OrgRow>
            <OrgRow
              title="Comp off type"
              desc={`Comp off fits under ${otTarget}’s yearly standard and never changes it`}
            >
              <div style={{ width: '10rem' }}>
                <Select
                  ariaLabel="Comp off type"
                  value={ot.targetType}
                  align="right"
                  options={targets.map((t) => ({ value: t.id, label: t.label }))}
                  onChange={(v) => setOT({ targetType: v })}
                />
              </div>
            </OrgRow>
            <OrgRow
              title="Max leaves per month"
              desc="Ceiling on leaves a single month can generate"
            >
              <Stepper
                label="Max leaves per month"
                value={ot.maxPerMonth}
                onChange={(v) => setOT({ maxPerMonth: v })}
                min={0}
                max={10}
              />
            </OrgRow>
            <OrgRow title="Max leaves per year" desc="Annual ceiling across all months" last>
              <Stepper
                label="Max leaves per year"
                value={ot.maxPerYear}
                onChange={(v) => setOT({ maxPerYear: v })}
                min={0}
                max={60}
              />
            </OrgRow>
          </fieldset>
        </PCard>

        {/* Nothing reads policy.approval or approvalLevels (UI-CONVENTIONS
            Deferred): how a request is routed is decided by its own flow, not by
            these two settings. They were live under a "Not enforced yet" badge,
            inviting edits that changed nothing; the owner's call (2026-10-08)
            is to show them off, with why. The switch stays a Tab stop that
            reads the reason out, as the overtime switch does. */}
        <PCard level={3} label="Requests & approvals">
          <div
            id={unenforcedId}
            style={{
              ...noteStyle,
              paddingBottom: 'var(--hf-space-3)',
              borderBottom: '1px solid var(--hf-rule)',
            }}
          >
            Not enforced yet: changing this does nothing.
          </div>
          <OrgRow title="Require approval" desc="Leave requests need a manager’s sign-off">
            <Toggle
              label="Require approval"
              checked={p.approval}
              disabled
              focusableWhenDisabled
              describedBy={unenforcedId}
            />
          </OrgRow>
          <OrgRow title="Approval levels" desc="Number of approvers in the chain" last>
            <Stepper
              label="Approval levels"
              value={p.approvalLevels}
              onChange={(v) => setP({ approvalLevels: v })}
              min={1}
              max={3}
              disabled
            />
          </OrgRow>
          {/* Notice period and probation length moved to Employment
              (policy.employment), where they have readers. Here they were
              steppers writing values nothing ever read. */}
        </PCard>
        {/* FR-8.5. Read nightly by sweep_escalations(); notify only. */}
        <PCard level={3} label="Escalation">
          <OrgRow
            title="Escalate after"
            desc="A leave request or time correction still waiting this many working days is flagged to HR and the approvers’ managers"
            last
          >
            <Stepper
              label="Escalate after"
              unit="days"
              value={p.escalation?.afterDays ?? 2}
              onChange={(v) => setP({ escalation: { afterDays: v } })}
              min={1}
              max={30}
            />
          </OrgRow>
        </PCard>
      </div>

      <PCard
        level={3}
        label="Working on a leave day"
        right={<span style={proseMeta}>settled at check-out · % of the standard day</span>}
      >
        <OrgRow
          title="Reconcile automatically"
          desc="If somebody works on approved leave, give the day back instead of consuming it"
        >
          <Toggle
            label="Reconcile automatically"
            checked={lc.enabled}
            onChange={(v) => setLC({ enabled: v })}
          />
        </OrgRow>
        <OrgRow
          title="Half day above"
          desc="Work at least this much and the leave becomes a half day"
        >
          <Stepper
            label="Half day above"
            unit="%"
            value={Math.round(lc.halfDayAt * 100)}
            onChange={(v) => setLC({ halfDayAt: v / 100 })}
            min={5}
            max={Math.round(lc.cancelAt * 100) - 5}
            step={5}
            disabled={!lc.enabled}
          />
        </OrgRow>
        <OrgRow
          title="Full day above"
          desc="Work at least this much and the whole leave day is returned"
          last
        >
          <Stepper
            label="Full day above"
            unit="%"
            value={Math.round(lc.cancelAt * 100)}
            onChange={(v) => setLC({ cancelAt: v / 100 })}
            min={Math.round(lc.halfDayAt * 100) + 5}
            max={100}
            step={5}
            disabled={!lc.enabled}
          />
        </OrgRow>
      </PCard>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// LEAVE · TYPES
// ═════════════════════════════════════════════════════════════════════════════
// A type is archived, never deleted (ORG-01). leave_balances, leave_requests,
// leave_day_exceptions and leave_ledger all reference leave_types, and the × and
// "Delete selected" that lived here erased every request, balance and ledger row
// of the type — under copy promising history was kept. An archived type keeps
// all of it and drops out of the pickers.
const colourName = (hex: string) =>
  TYPE_COLOURS.find((c) => c.value === hex.toUpperCase())?.label ??
  RETIRED_COLOURS[hex.toUpperCase()] ??
  'Custom';

// Where a key takes a radiogroup of n from radio `at`: the arrows step and wrap,
// Home and End go to the ends; -1 for any other key. SegmentedControl's step
// (components/card.tsx), copied: keep the two alike.
const radioStep = (key: string, at: number, n: number): number => {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[key];
  return step != null ? (at + step + n) % n : key === 'Home' ? 0 : key === 'End' ? n - 1 : -1;
};

// Arrow keys move between the radios; Space or Enter chooses the one in focus.
// For a radiogroup whose every choice is a write. A radiogroup selects as the
// arrows move, which on the access matrix wrote a live permission per
// keypress: reaching Full from None wrote View and Edit on the way, and one
// ArrowRight on Full wrapped round to None, unasked. Moving is not choosing
// there (UI-CONVENTIONS §8: access is committed explicitly), nor on a leave
// type's colour, where each choice is a save and a receipt. The focus still
// wraps, as every other radiogroup's does: moving writes nothing.
const moveWithoutChoosing = (e: KeyboardEvent<HTMLDivElement>) => {
  const radios = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')];
  const next = radioStep(
    e.key,
    radios.indexOf(document.activeElement as HTMLElement),
    radios.length,
  );
  if (next < 0) return;
  e.preventDefault();
  e.stopPropagation();
  radios[next]?.focus();
};

// Every colour on offer, each a named radio. A colour used to be changed by
// clicking the swatch to step through the palette: up to seven clicks, each one
// a write, with no way to see what was on offer (ORG-21). SegmentedControl's
// keyboard contract: one Tab stop, and the arrows and Home/End move and choose.
// The cursor and the disabled look are .hf-swatch's, in tokens.css: set inline,
// they beat the rule, and a read-only viewer saw live swatches.
const SwatchPicker = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string; swatch: CSSProperties }[];
  onChange: (v: string) => void;
}) => {
  const coarse = useCoarse();
  // A custom colour from before the palette is none of these: the first one
  // is then the Tab stop, with nothing checked.
  const at = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next = radioStep(e.key, at, options.length);
    if (next < 0) return;
    e.preventDefault();
    onChange(options[next].value);
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{ display: 'flex', gap: 'var(--hf-space-2)', flexWrap: 'wrap' }}
    >
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={i === at ? 0 : -1}
            className="hf-swatch hf-hit"
            aria-label={o.label}
            title={o.label}
            onClick={() => onChange(o.value)}
            // A radio under a finger grows to 44px tall for real (tokens.css),
            // so a swatch grows square with it rather than into a tall sliver.
            style={{
              width: coarse ? TOUCH : 'var(--hf-space-6)',
              height: coarse ? TOUCH : 'var(--hf-space-6)',
              padding: 'var(--hf-space-1)',
              borderRadius: 'var(--hf-radius-md)',
              background: 'transparent',
              // The chosen one is ringed in ink: a shape, not a second colour.
              border: `2px solid ${on ? 'var(--hf-ink)' : 'transparent'}`,
            }}
          >
            <span style={{ ...o.swatch, display: 'block', width: '100%', height: '100%' }} />
          </button>
        );
      })}
    </div>
  );
};
const typeColourOptions = TYPE_COLOURS.map((c) => ({ ...c, swatch: typeSwatch(c.value) }));

// A row's colour: the swatch opens the palette beside it, with the focus on the
// colour it has. Choosing here is a write, so the arrows only move; Space or
// Enter chooses, as on the access matrix.
const TypeColour = ({ t, onPick }: { t: LeaveType; onPick: (color: string) => void }) => {
  const pop = usePopover({ minWidth: 176 });
  const { open, panelRef } = pop;
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')?.focus();
  }, [open, panelRef]);
  return (
    <span style={{ display: 'inline-flex' }}>
      <button
        {...pop.triggerProps}
        type="button"
        className="hf-swatch hf-hit"
        aria-label={`Colour of ${t.label}: ${colourName(t.color)}`}
        title="Change colour"
        style={{
          ...typeSwatch(t.color),
          width: 'var(--hf-space-5)',
          height: 'var(--hf-space-5)',
          padding: 0,
        }}
      />
      {pop.open && (
        <div
          {...pop.panelProps}
          onKeyDownCapture={moveWithoutChoosing}
          style={{
            ...pop.panelProps.style,
            padding: 'var(--hf-space-3)',
            borderRadius: 'var(--hf-radius-card)',
            background: 'var(--hf-paper-2)',
            border: '1px solid var(--hf-rule-2)',
            boxShadow: 'var(--hf-shadow-pop)',
          }}
        >
          <SwatchPicker
            label={`Colour of ${t.label}`}
            value={t.color}
            options={typeColourOptions}
            onChange={(c) => {
              onPick(c);
              pop.close();
            }}
          />
        </div>
      )}
    </span>
  );
};

const typeRow = (last: boolean): CSSProperties => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-3) 0',
  borderBottom: last ? 'none' : '1px solid var(--hf-rule)',
});

const LtRow = ({
  t,
  last,
  editable,
  archiveBlock,
  onUpdate,
}: {
  t: LeaveType;
  last: boolean;
  /** Whether the viewer can change the org's settings (org:full). */
  editable: boolean;
  /** Why this type can't be archived right now, if it can't. */
  archiveBlock?: string;
  /** Resolves to whether the change was saved. */
  onUpdate: (delta: Partial<LeaveType>, message?: string) => Promise<boolean>;
}) => {
  const coarse = useCoarse();
  // A rename the field can answer for: the draft stays open with the reason if
  // it is blank or the write is refused, and "Saved" appears only once it is.
  const rename = async (key: 'label' | 'short', v: string): Promise<SaveResult> => {
    const next = key === 'short' ? v.trim().toUpperCase().slice(0, 3) : v.trim();
    if (!next) return { error: `A leave type needs a ${key === 'short' ? 'code' : 'name'}.` };
    const saved = await onUpdate({ [key]: next });
    return saved ? {} : { error: 'Not saved. The notice on this page says why.' };
  };
  if (t.archived)
    return (
      <div style={typeRow(last)}>
        <span
          style={{
            ...typeSwatch(t.color),
            width: 'var(--hf-space-5)',
            height: 'var(--hf-space-5)',
          }}
        />
        <span style={{ flex: '1 1 8rem', fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink-2)' }}>
          {t.label} <Mono dim>{t.short}</Mono>
        </span>
        <Badge tone="neutral">archived</Badge>
        {editable && (
          <Button
            size="sm"
            variant="ghost"
            style={rowAction(coarse)}
            onClick={() => void onUpdate({ archived: false }, `${t.label} is back in use.`)}
          >
            Unarchive
          </Button>
        )}
      </div>
    );
  return (
    <div style={typeRow(last)}>
      <TypeColour t={t} onPick={(color) => void onUpdate({ color })} />
      <div style={{ flex: '1 1 8rem', minWidth: 0 }}>
        <EditableField
          label={`Name of ${t.label}`}
          hideLabel
          value={t.label}
          onSaveAsync={(v) => rename('label', v)}
        />
      </div>
      <div style={{ width: '4rem' }}>
        <EditableField
          label={`Code for ${t.label}`}
          hideLabel
          value={t.short}
          onSaveAsync={(v) => rename('short', v)}
          mono
        />
      </div>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--hf-space-2)',
          // Holds the column: "Unpaid" is wider than "Paid".
          minWidth: '5.75rem',
          fontSize: 'var(--hf-text-sm)',
          color: 'var(--hf-ink-2)',
        }}
      >
        {/* Unpaid keeps the day count, shown disabled. It used to write 0, and
            turning Paid back on restored a hardcoded 10: a 20-day entitlement
            came back as 10 (ORG-21). */}
        <Toggle
          label={`${t.label} is paid`}
          checked={t.paid}
          onChange={(paid) => void onUpdate({ paid })}
        />
        {t.paid ? 'Paid' : 'Unpaid'}
      </label>
      <Stepper
        label={`${t.label} days per year`}
        unit="days / yr"
        value={t.days_per_year}
        onChange={(v) => void onUpdate({ days_per_year: v })}
        min={0}
        max={180}
        /* 12.5 days a year is a real entitlement; the column is numeric. */
        step={0.5}
        disabled={!t.paid}
      />
      {/* Archive only where it can act; a type in use says why instead. A
          disabled button beside its reason said the same thing twice. A
          viewer who can't edit gets neither the button nor its room: no row
          has one to line up with. */}
      {!editable ? (
        archiveBlock && <div style={{ ...noteStyle, flexBasis: '100%' }}>{archiveBlock}</div>
      ) : archiveBlock ? (
        <>
          {/* The button's width, kept with no height and unseen, so this
              row's code, Paid and days line up with the rows above and
              below; without it they slid right by an Archive. */}
          <span
            aria-hidden="true"
            style={{ ...rowAction(false), height: 0, overflow: 'hidden', visibility: 'hidden' }}
          >
            <Button size="sm" disabled>
              Archive
            </Button>
          </span>
          <div style={{ ...noteStyle, flexBasis: '100%' }}>{archiveBlock}</div>
        </>
      ) : (
        <Button
          size="sm"
          style={rowAction(coarse)}
          onClick={() => void onUpdate({ archived: true }, `Archived ${t.label}.`)}
        >
          Archive
        </Button>
      )}
    </div>
  );
};

export const OrgLeaveTypesTab = ({
  leaveTypes,
  overtimeTarget,
  editable,
  onAdd,
  onUpdate,
}: {
  leaveTypes: LeaveType[];
  /** The type comp off is recorded against, while comp off is on. */
  overtimeTarget?: string;
  /** Whether the viewer can change the org's settings (org:full). */
  editable: boolean;
  /** Resolves to whether the type was added. */
  onAdd: (t: Omit<LeaveType, 'rank'>) => Promise<boolean>;
  onUpdate: (id: string, delta: Partial<LeaveType>, message?: string) => Promise<boolean>;
}) => {
  // A new type opens on a colour no type in use has, not on Annual's.
  const inUse = leaveTypes.filter((t) => !t.archived).map((t) => t.color.toUpperCase());
  const blank = { label: '', short: '', color: nextColour(inUse), paid: true, days: 12 };
  const [draft, setDraft] = useState(blank);
  const [adding, submit] = useSubmit();
  const [open, setOpen] = useState(false);
  const opener = useId();
  // Archived last. sort is stable, so both groups keep their rank order.
  const rows = [...leaveTypes].sort((a, b) => Number(!!a.archived) - Number(!!b.archived));
  // What a new hire starts with. A card on Leave policy showed each type's days
  // again, at 24px above the steppers that set them; this total is what it
  // added.
  const paidDays = rows
    .filter((t) => !t.archived && t.paid)
    .reduce((n, t) => n + t.days_per_year, 0);
  const label = draft.label.trim();
  // The next colour stays chosen: it moves on with each type added.
  const close = () => {
    setOpen(false);
    setDraft((d) => ({ ...blank, color: d.color }));
  };
  const add = async () => {
    if (!label) return;
    const added = await submit(() =>
      onAdd({
        id: 't' + Date.now(),
        label,
        short: (draft.short.trim() || label.slice(0, 2)).toUpperCase().slice(0, 3),
        color: draft.color,
        paid: draft.paid,
        days_per_year: draft.days,
      }),
    );
    if (!added) return;
    setDraft({ ...blank, color: nextColour([...inUse, draft.color], draft.color) });
    setOpen(false);
  };

  // No card title: "Leave types" was the section's title again. Add, Archive
  // and Unarchive show only where they can act, as on Departments and Event
  // types: a read-only viewer (the seeded HR role has org:edit, not org:full)
  // saw each one greyed by the fieldset (critique 2026-10-06 #4).
  return (
    <PCard level={3}>
      <ListHead count={listCount(rows, 'type')}>
        {editable && !open && (
          <AddOpener id={opener} onClick={() => setOpen(true)}>
            Add leave type
          </AddOpener>
        )}
      </ListHead>
      {editable && open && (
        <DisclosedForm opener={opener}>
          <form
            aria-label="Add leave type"
            className="hf-panel"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
            style={addPanel}
          >
            <div style={fieldRow}>
              <Field label="Name" required style={{ flex: '2 1 14rem' }}>
                <TextInput
                  value={draft.label}
                  onChange={(v) => setDraft({ ...draft, label: v })}
                  placeholder="e.g. Study leave"
                />
              </Field>
              <Field
                label="Short code"
                hint="Up to three letters. Left empty, it is the first two of the name."
                style={{ flex: '1 1 12rem' }}
              >
                <TextInput
                  value={draft.short}
                  onChange={(v) => setDraft({ ...draft, short: v.toUpperCase().slice(0, 3) })}
                  mono
                />
              </Field>
            </div>
            <div style={{ ...fieldRow, alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
                <Eyebrow>Colour</Eyebrow>
                <SwatchPicker
                  label="Colour"
                  value={draft.color}
                  options={typeColourOptions}
                  onChange={(color) => setDraft({ ...draft, color })}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-3)',
                }}
              >
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--hf-space-2)',
                    fontSize: 'var(--hf-text-sm)',
                    color: 'var(--hf-ink-2)',
                  }}
                >
                  <Toggle
                    label="Paid"
                    checked={draft.paid}
                    onChange={(v) => setDraft({ ...draft, paid: v })}
                  />
                  Paid
                </label>
                <Stepper
                  label="Days per year"
                  unit="days / yr"
                  value={draft.days}
                  onChange={(v) => setDraft({ ...draft, days: v })}
                  min={0}
                  max={180}
                  step={0.5}
                  disabled={!draft.paid}
                />
              </div>
            </div>
            <AddActions
              busy={adding}
              reason={label ? undefined : 'Needs a name before it can be added.'}
              onCancel={close}
            >
              Add leave type
            </AddActions>
          </form>
        </DisclosedForm>
      )}
      {rows.length === 0 && (
        <EmptyState title="No leave types yet">
          {editable ? 'Add the first one with Add leave type.' : undefined}
        </EmptyState>
      )}
      {rows.map((t, i) => (
        <LtRow
          key={t.id}
          t={t}
          last={i === rows.length - 1}
          editable={editable}
          archiveBlock={
            t.id !== overtimeTarget
              ? undefined
              : editable
                ? 'Comp off is recorded against this type. Choose another under Leave policy → Comp off type to archive it.'
                : 'Comp off is recorded against this type.'
          }
          onUpdate={(delta, message) => onUpdate(t.id, delta, message)}
        />
      ))}
      {/* Not under the empty state, where it was "0 paid days a year". */}
      {rows.length > 0 && (
        <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
          A new hire starts with {paidDays} paid days a year across the types in use. Existing
          employees keep their individual balances.
        </div>
      )}
      <div style={{ ...noteStyle, marginTop: 'var(--hf-space-2)' }}>
        Archiving a type keeps every request, balance and ledger entry booked against it, and takes
        it off the list people choose from. A type can’t be deleted.
      </div>
    </PCard>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// ACCESS · ROLES — overview, reporting tree, per-role access
// ═════════════════════════════════════════════════════════════════════════════
const levelIndex = (l: PermLevel) => ORG_ACCESS_LEVELS.findIndex((x) => x.id === l);
const levelLabel = (l: PermLevel) => ORG_ACCESS_LEVELS[Math.max(0, levelIndex(l))].label;
const moduleMeta = (m: string) =>
  ORG_MODULE_META[m] ?? { label: m, icon: 'grid' as const, desc: '' };
const LEVEL_OPTIONS = ORG_ACCESS_LEVELS.map((l) => ({ value: l.id, label: l.label }));
// The modules people can open in this build, and the rest. A level on a module
// outside the build still applies: the database enforces it whatever the
// sidebar shows (ORG-11).
const BUILD_MODULES = MODULES.filter(isModuleEnabled);
const OTHER_MODULES = MODULES.filter((m) => !isModuleEnabled(m));

// What a raise lets a role do that the level's name doesn't say. Each line is
// shown when the change crosses its level from below.
const RAISE_NOTES: Record<string, Partial<Record<PermLevel, string>>> = {
  people: {
    view: 'Opens the whole people directory.',
    full: 'Shows salaries, bank details and payroll for everyone in the company, and lets them decide leave requests.',
  },
  talent: {
    view: 'Also opens the whole people directory, even though Talent is not in this build.',
  },
  org: {
    full: 'Lets them change every setting on this screen, including any role’s access, and read bug reports.',
  },
};
type Levels = Record<string, PermLevel>;
/** The lines a confirm shows for the modules in `next` that go UP from `now`,
 *  and whether any of them crosses a level RAISE_NOTES speaks for. */
const raiseOf = (now: Levels, next: Levels): { lines: string[]; noted: boolean } => {
  let noted = false;
  const lines = Object.entries(next).flatMap(([m, to]) => {
    const from = now[m] ?? 'none';
    if (levelIndex(to) <= levelIndex(from)) return [];
    const notes = ORG_ACCESS_LEVELS.filter(
      (l) => levelIndex(l.id) > levelIndex(from) && levelIndex(l.id) <= levelIndex(to),
    ).flatMap((l) => RAISE_NOTES[m]?.[l.id] ?? []);
    noted ||= notes.length > 0;
    return [`${moduleMeta(m).label}: ${levelLabel(from)} → ${levelLabel(to)}.`, ...notes];
  });
  return { lines, noted };
};

// A change that waits on a confirm: a raise, or a delete (ORG-05).
type Pending = {
  title: string;
  consequence: string[];
  confirmLabel: string;
  tone?: 'danger';
  run: () => void;
};
// Raising access confirms, with what it lets the role do and how many people
// that reaches; lowering applies at once. One misclick on "Full" in the People
// row used to hand every Member the company's salaries (ORG-05). Where a raise
// opens what the level's name doesn't say (salaries, this screen), the dialog is
// a danger one: it starts on Cancel, so Enter on open is not the grant. Null
// when nothing in `next` goes up: then there is nothing to ask. `heads` is
// null when the roster can't be read.
const askRaise = (
  ask: Pick<Pending, 'title' | 'confirmLabel' | 'run'>,
  now: Levels,
  next: Levels,
  heads: number | null,
): Pending | null => {
  const { lines, noted } = raiseOf(now, next);
  if (!lines.length) return null;
  const who =
    heads == null
      ? 'Everyone who holds this role gets it straight away.'
      : heads === 0
        ? 'Nobody holds this role yet; whoever is given it gets this access.'
        : `${heads} ${heads === 1 ? 'person holds' : 'people hold'} this role and ${heads === 1 ? 'gets' : 'get'} it straight away.`;
  return { ...ask, consequence: [...lines, who], tone: noted ? 'danger' : undefined };
};

const COMP_NOTE = 'Compensation, bank details and payroll are visible only at People: Full.';

// Full, the top step, also takes weight: after "any access at all", it is the
// one a reader scans for (People: Full sees salaries).
const LevelCell = ({ level }: { level: PermLevel }) => {
  const l = ORG_ACCESS_LEVELS[Math.max(0, levelIndex(level))];
  return (
    <span
      style={{
        fontSize: 'var(--hf-text-sm)',
        color: l.ink,
        fontWeight: level === 'full' ? 'var(--hf-weight-medium)' : undefined,
      }}
    >
      {l.label}
    </span>
  );
};

// Every role against every module in the build, on one screen. Checking who can
// see compensation used to mean selecting each of nine roles in turn and
// reading one row of each (ORG-11).
const AccessOverview = ({
  roles,
  permsByRole,
  selId,
  setSelId,
}: {
  roles: Role[];
  permsByRole: Record<string, Levels>;
  selId: string;
  setSelId: (id: string) => void;
}) => {
  const cell: CSSProperties = {
    padding: 'var(--hf-space-2) var(--hf-space-3)',
    textAlign: 'left',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    borderTop: '1px solid var(--hf-rule)',
  };
  return (
    <PCard
      level={3}
      label="Who can do what"
      right={<span style={proseMeta}>choose a role to change its access</span>}
    >
      {/* A wide table scrolls inside its card, never the page. */}
      <div
        className="hf-scroll"
        role="region"
        aria-label="Access by role"
        tabIndex={0}
        style={{ overflowX: 'auto' }}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th scope="col" style={{ ...cell, borderTop: 0 }}>
                <Eyebrow>Module</Eyebrow>
              </th>
              {roles.map((r) => (
                <th key={r.id} scope="col" style={{ ...cell, borderTop: 0 }}>
                  <Button
                    size="sm"
                    className="hf-hit"
                    variant={r.id === selId ? 'accent' : 'ghost'}
                    ariaLabel={`${r.label}: show this role’s access`}
                    onClick={() => setSelId(r.id)}
                  >
                    {r.label}
                  </Button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BUILD_MODULES.map((m) => (
              <tr key={m}>
                <th
                  scope="row"
                  style={{
                    ...cell,
                    fontWeight: 'inherit',
                    fontSize: 'var(--hf-text-sm)',
                    color: 'var(--hf-ink)',
                  }}
                >
                  {moduleMeta(m).label}
                </th>
                {roles.map((r) => (
                  <td key={r.id} style={cell}>
                    <LevelCell level={permsByRole[r.id]?.[m] ?? 'none'} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
        {COMP_NOTE}
        {OTHER_MODULES.length > 0 &&
          ` ${OTHER_MODULES.length} more modules are not in this build; each role lists them below.`}
      </div>
    </PCard>
  );
};

const BLANK_ROLE = { label: '', desc: '', parent: 'admin', tone: 'sky' };
// A custom role's id is its name, slugged: "Finance-Manager" and "Finance
// Manager" are one id, which the form has to know before it is sent.
const roleSlug = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
const roleColourOptions = ORG_ROLE_PALETTE.map((tn) => ({
  value: tn,
  label: tn === 'neutral' ? 'Grey' : tn[0].toUpperCase() + tn.slice(1),
  swatch: {
    borderRadius: 'var(--hf-radius-sm)',
    background: TONE[tn].bg,
    border: `1.5px solid ${TONE[tn].fg}`,
  } as CSSProperties,
}));

// ?role= set to a role, or taken off.
const withRole = (id: string | null) => (prev: URLSearchParams) => {
  const next = new URLSearchParams(prev);
  if (id) next.set('role', id);
  else next.delete('role');
  return next;
};

export const OrgRolesTab = ({
  editable,
  roles,
  permsByRole,
  headcounts,
  selId,
  setSelId,
  onAddRole,
  onSetPerm,
  onReparent,
  onCopyParent,
  onRemoveRole,
}: {
  /** The role list stays selectable either way — reading a role's access is viewing. */
  editable: boolean;
  roles: Role[];
  permsByRole: Record<string, Levels>;
  /** People per role. null when the viewer can't read the roster: a count of
   *  the rows they can see would state "nobody holds this role" as a fact. */
  headcounts: Record<string, number> | null;
  selId: string;
  setSelId: (id: string) => void;
  /** Resolves to whether the role was created. */
  onAddRole: (draft: {
    label: string;
    desc: string;
    parent: string;
    tone: string;
  }) => Promise<boolean>;
  onSetPerm: (roleId: string, module: string, level: PermLevel) => void;
  onReparent: (roleId: string, parentId: string | null) => void;
  onCopyParent: (roleId: string) => void;
  onRemoveRole: (roleId: string) => void;
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK_ROLE);
  const [creating, submit] = useSubmit();
  const opener = useId();
  const closeAdd = () => {
    setAdding(false);
    setDraft(BLANK_ROLE);
  };
  const [pending, setPending] = useState<Pending | null>(null);
  const coarse = useCoarse();

  const byId = (id: string | null) => roles.find((r) => r.id === id);
  // Below md, list → detail (UI-CONVENTIONS §8): the role list alone, then one
  // role's access alone. The tree beside a matrix of nine roles was two screens
  // squeezed into one column. Which role is open is ?role=, so the phone's own
  // back gesture returns to the list. The overview table is for a desktop.
  const phone = useBelow('md');
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const asked = params.get('role');
  const opened = phone ? byId(asked) : undefined;
  const openId = opened?.id;
  const sel = opened || byId(selId) || roles[0];
  // One role on screen, whichever layout shows it. The phone's ?role= becomes
  // the selection; on a desktop a ?role= the selection has moved past is
  // dropped. Two sources let a rotation swap which role's levels were on screen
  // under a finger, and a narrowed window open a role nobody had just picked.
  useEffect(() => {
    if (!asked) return;
    if (phone) {
      if (openId && openId !== selId) setSelId(openId);
    } else if (asked !== selId) setParams(withRole(null), { replace: true });
  }, [phone, asked, openId, selId, setSelId, setParams]);
  const openRole = (id: string) => {
    setSelId(id);
    if (phone) setParams(withRole(id), { state: { fromRoleList: true } });
  };
  // Back to the entry the list pushed from, so Back and the back gesture agree;
  // a role opened from a link has no list behind it, so the list replaces it.
  const backToList = () =>
    (location.state as { fromRoleList?: boolean } | null)?.fromRoleList
      ? navigate(-1)
      : setParams(withRole(null), { replace: true });
  // The view swaps under the focus on a phone: opening a role unmounts the row
  // pressed, and the list comes back without the detail's Back. Focus goes to
  // the detail's Back, then to the row of the role that was open (or, after a
  // delete, the role it reported into), as the shell's Menu hands it to #main.
  // Not on a resize: the access card stays mounted, and so does its focus.
  const backRef = useRef<HTMLDivElement>(null);
  const selRowRef = useRef<HTMLButtonElement>(null);
  const shown = useRef({ phone, openId });
  useEffect(() => {
    const was = shown.current;
    shown.current = { phone, openId };
    if (!phone || !was.phone || was.openId === openId) return;
    if (openId) backRef.current?.querySelector('button')?.focus();
    else selRowRef.current?.focus();
  }, [phone, openId]);

  // build flattened tree with rail metadata for connector lines
  const flat: { role: Role; depth: number; rails: boolean[]; isLast: boolean }[] = [];
  const listed = new Set<string>();
  const buildFlat = (parentId: string | null, rails: boolean[]) => {
    const sibs = roles.filter((r) => r.parent_id === parentId && !listed.has(r.id));
    sibs.forEach((r, i) => {
      const isLast = i === sibs.length - 1;
      listed.add(r.id);
      flat.push({ role: r, depth: rails.length, rails: rails.slice(), isLast });
      buildFlat(r.id, [...rails, !isLast]);
    });
  };
  buildFlat(null, []);
  // Roles in a parent cycle hang off no top-level role, and were left out: a
  // phone could not open one to move it out. Listed at the top level instead.
  for (const r of roles)
    if (!listed.has(r.id)) {
      listed.add(r.id);
      flat.push({ role: r, depth: 0, rails: [], isLast: true });
      buildFlat(r.id, [false]);
    }

  if (!sel) return null;
  const selPerms = permsByRole[sel.id] || {};
  const isOwner = sel.id === 'owner';

  // delete guards for the selected role
  const selKids = roles.filter((r) => r.parent_id === sel.id).length;
  const selHeads = headcounts ? headcounts[sel.id] || 0 : null;
  const isRoot = sel.parent_id === null;
  // Deleting a role drops whoever holds it to Member (profiles.role_id is `on
  // delete set null`), so without the roster there is no telling it is safe.
  const delReason = isRoot
    ? 'The top-level role cannot be removed'
    : selKids
      ? `Has ${selKids} reporting role${selKids > 1 ? 's' : ''} · move them first`
      : selHeads == null
        ? 'Who holds this role isn’t visible to you, so it can’t be deleted from here'
        : selHeads
          ? `${selHeads} ${selHeads > 1 ? 'people are' : 'person is'} assigned this role · reassign them first`
          : '';

  // parent options for the selected role's "reports into" (exclude self + descendants)
  const blocked = new Set([sel.id, ...descendantsOf(roles, sel.id)]);
  const parentOpts: SelectOption[] = [
    { value: '__root', label: '— Top level (no manager) —' },
    ...roles.filter((r) => !blocked.has(r.id)).map((r) => ({ value: r.id, label: r.label })),
  ];
  const parent = byId(sel.parent_id);

  // access summary for the selected role
  const levelCounts = ORG_ACCESS_LEVELS.map((l) => ({
    ...l,
    n: MODULES.filter((m) => (selPerms[m] || 'none') === l.id).length,
  }));

  const requestPerm = (module: string, level: PermLevel) => {
    const run = () => onSetPerm(sel.id, module, level);
    const ask = askRaise(
      { title: `Raise ${sel.label}’s access?`, confirmLabel: `Raise to ${levelLabel(level)}`, run },
      selPerms,
      { [module]: level },
      selHeads,
    );
    return ask ? setPending(ask) : run();
  };
  const requestCopy = () => {
    if (!parent) return;
    const theirs = Object.fromEntries(
      MODULES.map((m) => [m, permsByRole[parent.id]?.[m] ?? 'none']),
    );
    const run = () => onCopyParent(sel.id);
    const ask = askRaise(
      { title: `Give ${sel.label} ${parent.label}’s access?`, confirmLabel: 'Copy access', run },
      selPerms,
      theirs,
      selHeads,
    );
    return ask ? setPending(ask) : run();
  };
  const requestDelete = () =>
    setPending({
      title: `Delete ${sel.label}?`,
      consequence: [
        'The role and its access levels are deleted. This can’t be undone.',
        'Nobody holds it, and no role reports into it.',
      ],
      confirmLabel: 'Delete role',
      tone: 'danger',
      run: () => {
        onRemoveRole(sel.id);
        // Its detail has nothing left to show, and a Back to it would be a dead step.
        if (opened) backToList();
      },
    });

  const roleName = draft.label.trim();
  // Not while it is being created: the new role is in `roles` a moment before
  // the form closes, and would read as a clash with itself.
  const taken =
    !creating &&
    roles.some((r) => sameName(r.label, roleName) || (!!roleName && r.id === roleSlug(roleName)));
  const create = async () => {
    if (!roleName || taken || !(await submit(() => onAddRole(draft)))) return;
    closeAdd();
    // The desktop shows the new role's access beside the list; a phone opens it.
    const id = roleSlug(roleName);
    if (phone && id) openRole(id);
  };

  // A level's indent. A phone's narrower one leaves the name room three levels
  // down, where at 320px it read "HR Mana…" and "Project L…".
  const RAIL = phone ? 16 : 24;
  const moduleRow = (m: string, last: boolean) => {
    const meta = moduleMeta(m);
    const lvl = selPerms[m] || 'none';
    return (
      <div
        key={m}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--hf-space-2) var(--hf-space-3)',
          padding: 'var(--hf-space-3) 0',
          borderBottom: last ? 'none' : '1px solid var(--hf-rule)',
        }}
      >
        <div
          style={{
            width: 'var(--hf-space-6)',
            height: 'var(--hf-space-6)',
            borderRadius: 'var(--hf-radius-md)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // The level's ink, as on the matrix; the segmented control
            // beside it says which level.
            background: 'var(--hf-surface)',
            border: '1px solid var(--hf-rule)',
            color: ORG_ACCESS_LEVELS[Math.max(0, levelIndex(lvl))].ink,
          }}
        >
          <Icon name={meta.icon} size={15} />
        </div>
        <div style={{ flex: '1 1 10rem', minWidth: 0 }}>
          <div style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>{meta.label}</div>
          <div style={noteStyle}>{meta.desc}</div>
        </div>
        {/* A radiogroup: which level is chosen is state, not a colour (ORG-06). */}
        {/* A phone gives the level its own full row, the chips sharing it. */}
        <div
          onKeyDownCapture={moveWithoutChoosing}
          style={phone ? { flexBasis: '100%' } : undefined}
        >
          <SegmentedControl
            label={`${meta.label} access for ${sel.label}`}
            value={lvl}
            options={LEVEL_OPTIONS}
            onChange={(v) => v !== lvl && requestPerm(m, v)}
            full={phone}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={stack}>
      {!phone && (
        <AccessOverview
          roles={roles}
          permsByRole={permsByRole}
          selId={sel.id}
          setSelId={setSelId}
        />
      )}
      {opened && (
        <div ref={backRef}>
          <Button
            variant="ghost"
            size="sm"
            icon="chevronLeft"
            className="hf-hit"
            onClick={backToList}
          >
            All roles
          </Button>
        </div>
      )}

      <div style={phone ? stack : splitRow}>
        {/* ── role hierarchy tree ── */}
        {!opened && (
          <div style={phone ? stack : splitCol(4)}>
            <PCard
              level={3}
              label="Role hierarchy"
              right={
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hf-space-3)' }}>
                  <span style={metaStyle}>{roles.length} roles</span>
                  {/* Org's other add buttons' paint and words: this was an
                      accent "+ Role" that toggled, beside four secondary "+ Add …"
                      on the next tabs. Off, not gone, for a read-only viewer. */}
                  {!adding && (
                    <AddOpener id={opener} disabled={!editable} onClick={() => setAdding(true)}>
                      Add role
                    </AddOpener>
                  )}
                </div>
              }
            >
              {adding && (
                <DisclosedForm opener={opener}>
                  <form
                    aria-label="Add role"
                    className="hf-panel"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void create();
                    }}
                    style={addPanel}
                  >
                    <Field
                      label="Name"
                      required
                      error={taken ? 'A role with that name already exists.' : undefined}
                    >
                      <TextInput
                        value={draft.label}
                        onChange={(v) => setDraft({ ...draft, label: v })}
                        placeholder="e.g. Finance Manager"
                      />
                    </Field>
                    <Field label="Description">
                      <TextArea
                        value={draft.desc}
                        rows={2}
                        onChange={(v) => setDraft({ ...draft, desc: v })}
                        placeholder="e.g. Runs payroll and vendor payments"
                      />
                    </Field>
                    <Field label="Reports into" hint="A new role starts with this role’s access.">
                      {(c) => (
                        <Select
                          {...c}
                          value={draft.parent}
                          options={roles.map((r) => ({ value: r.id, label: r.label }))}
                          onChange={(v) => setDraft({ ...draft, parent: v })}
                        />
                      )}
                    </Field>
                    <div
                      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}
                    >
                      <Eyebrow>Colour</Eyebrow>
                      <SwatchPicker
                        label="Colour"
                        value={draft.tone}
                        options={roleColourOptions}
                        onChange={(tone) => setDraft({ ...draft, tone })}
                      />
                    </div>
                    {/* Create used to close the form and throw the draft away
                        when the name was empty (ORG-13). */}
                    <AddActions
                      busy={creating}
                      reason={roleName ? undefined : 'Needs a name before it can be created.'}
                      blocked={taken}
                      onCancel={closeAdd}
                    >
                      Create role
                    </AddActions>
                  </form>
                </DisclosedForm>
              )}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {flat.map(({ role, depth, rails, isLast }) => {
                  const c = TONE[orgRoleTone(role)];
                  // Nothing is open while a phone shows the list, so nothing is
                  // current; the row of the role just closed has the focus.
                  const on = !phone && role.id === sel.id;
                  const hc = headcounts ? headcounts[role.id] || 0 : null;
                  return (
                    <div
                      key={role.id}
                      // The row's 4px margins leave the role's button 40px of
                      // it; under a finger, 44.
                      style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        minHeight: coarse ? TOUCH + 8 : 48,
                      }}
                    >
                      {rails.map((hasNext, i) => (
                        <div key={i} style={{ width: RAIL, flexShrink: 0, position: 'relative' }}>
                          {hasNext && (
                            <span
                              style={{
                                position: 'absolute',
                                left: RAIL / 2,
                                top: 0,
                                bottom: 0,
                                width: 1,
                                background: 'var(--hf-rule-2)',
                              }}
                            />
                          )}
                        </div>
                      ))}
                      {depth > 0 && (
                        <div style={{ width: RAIL, flexShrink: 0, position: 'relative' }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: RAIL / 2,
                              top: 0,
                              height: isLast ? '50%' : '100%',
                              width: 1,
                              background: 'var(--hf-rule-2)',
                            }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              left: RAIL / 2,
                              top: '50%',
                              width: RAIL / 2 - 2,
                              height: 1,
                              background: 'var(--hf-rule-2)',
                            }}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        ref={role.id === sel.id ? selRowRef : undefined}
                        aria-current={on ? 'true' : undefined}
                        onClick={() => openRole(role.id)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--hf-space-3)',
                          padding: 'var(--hf-space-2) var(--hf-space-3)',
                          margin: 'var(--hf-space-1) 0',
                          borderRadius: 'var(--hf-radius-md)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'inherit',
                          background: on ? 'var(--hf-accent-wash)' : 'var(--hf-surface)',
                          border: `1px solid ${on ? 'var(--hf-accent-line)' : 'var(--hf-rule-2)'}`,
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 'var(--hf-radius-xs)',
                            flexShrink: 0,
                            background: c.bg,
                            border: `1.5px solid ${c.fg}`,
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--hf-space-2)',
                          }}
                        >
                          {/* Wraps, between words first, rather than cut off. */}
                          <span
                            style={{
                              fontSize: 'var(--hf-text-md)',
                              color: 'var(--hf-ink)',
                              minWidth: 0,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {role.label}
                          </span>
                          {/* Neutral: the top of the tree is a position, not an error. */}
                          {role.parent_id === null && <Badge tone="neutral">top level</Badge>}
                          {role.is_custom && <Badge tone="neutral">custom</Badge>}
                        </span>
                        {hc != null && (
                          <span
                            style={{
                              ...metaStyle,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 'var(--hf-space-1)',
                              flexShrink: 0,
                            }}
                          >
                            <Icon name="users" size={12} />
                            {hc}
                            <span className="hf-sr-only">{hc === 1 ? 'person' : 'people'}</span>
                          </span>
                        )}
                        <Icon
                          name="chevronRight"
                          size={13}
                          style={{
                            color: on ? 'var(--hf-accent-text)' : 'var(--hf-ink-3)',
                            flexShrink: 0,
                          }}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  ...noteStyle,
                  marginTop: 'var(--hf-space-3)',
                  paddingTop: 'var(--hf-space-3)',
                  borderTop: '1px solid var(--hf-rule)',
                }}
              >
                {/* "Select a role to configure its access" said again what the
                    overview's meta and each row's chevron already say. */}
                To move a role in the tree, change its Reports into.
              </div>
            </PCard>
          </div>
        )}

        {/* ── access for the selected role ── */}
        {(!phone || opened) && (
          <fieldset
            disabled={!editable}
            style={{ ...bareFieldset, ...(phone ? stack : splitCol(5)) }}
          >
            <PCard
              level={3}
              label={`Access · ${sel.label}`}
              // A count, in the meta the hierarchy's "9 roles" uses; it was a
              // badge dotted in the role's own colour.
              right={
                selHeads != null && (
                  <span style={metaStyle}>
                    {selHeads} {selHeads === 1 ? 'person' : 'people'}
                  </span>
                )
              }
            >
              {sel.description && (
                <div style={{ ...noteStyle, marginBottom: 'var(--hf-space-2)' }}>
                  {sel.description}
                </div>
              )}

              <OrgRow
                title="Reports into"
                desc={
                  isRoot ? 'This is the top-level role' : 'Where this role sits in the hierarchy'
                }
              >
                {isRoot ? (
                  <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
                    Top level
                  </span>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 'var(--hf-space-2)',
                    }}
                  >
                    {parent && !isOwner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hf-hit"
                        title={`Set every level to ${parent.label}’s`}
                        onClick={requestCopy}
                      >
                        Copy access
                      </Button>
                    )}
                    <div style={{ width: '12rem' }}>
                      <Select
                        ariaLabel="Reports into"
                        value={sel.parent_id || '__root'}
                        align="right"
                        options={parentOpts}
                        onChange={(v) => onReparent(sel.id, v === '__root' ? null : v)}
                      />
                    </div>
                  </div>
                )}
              </OrgRow>

              {/* Owner always keeps full access: its levels are shown, not offered. */}
              <fieldset disabled={isOwner} style={bareFieldset}>
                <Eyebrow as="h4" style={{ padding: 'var(--hf-space-3) 0 var(--hf-space-1)' }}>
                  In this build
                </Eyebrow>
                {BUILD_MODULES.map((m, i) => moduleRow(m, i === BUILD_MODULES.length - 1))}
                {/* The overview's footnote, which a phone doesn't show. */}
                {phone && <div style={noteStyle}>{COMP_NOTE}</div>}
                {OTHER_MODULES.length > 0 && (
                  <details style={{ marginTop: 'var(--hf-space-3)' }}>
                    <summary style={summaryStyle(coarse)}>
                      Not in this build ({OTHER_MODULES.length}) — still enforced by the database
                    </summary>
                    <div style={{ ...noteStyle, marginTop: 'var(--hf-space-2)' }}>
                      Nobody can open these screens yet, but a level set here already decides what
                      the role can read.
                    </div>
                    {OTHER_MODULES.map((m, i) => moduleRow(m, i === OTHER_MODULES.length - 1))}
                  </details>
                )}
                <DashboardAccess roleId={sel.id} roleLabel={sel.label} />
              </fieldset>
              {isOwner && (
                <div style={{ ...noteStyle, marginTop: 'var(--hf-space-2)' }}>
                  The Owner role is protected — it always retains full access.
                </div>
              )}

              {/* summary + delete */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--hf-space-3)',
                  marginTop: 'var(--hf-space-4)',
                  paddingTop: 'var(--hf-space-4)',
                  borderTop: '1px solid var(--hf-rule)',
                  flexWrap: 'wrap',
                }}
              >
                {levelCounts.map((l) => (
                  <span key={l.id} style={metaStyle}>
                    {l.label} {l.n}
                  </span>
                ))}
                {/* Pushed to the end, not after a spacer: on a phone the row
                    wraps, and a spacer left Delete at the start of its line,
                    across the card from the reason it is blocked. */}
                <Button
                  variant="danger"
                  size="sm"
                  icon="x"
                  className="hf-hit"
                  style={{ marginLeft: 'auto' }}
                  disabled={!!delReason}
                  onClick={requestDelete}
                >
                  Delete role
                </Button>
              </div>
              {delReason && (
                <div style={{ ...noteStyle, marginTop: 'var(--hf-space-2)', textAlign: 'right' }}>
                  {delReason}
                </div>
              )}
            </PCard>
          </fieldset>
        )}
      </div>

      <ConfirmDialog
        open={!!pending}
        title={pending?.title ?? ''}
        consequence={pending?.consequence ?? []}
        confirmLabel={pending?.confirmLabel ?? ''}
        tone={pending?.tone}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          pending?.run();
          setPending(null);
        }}
      />
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN SCREEN
// ═════════════════════════════════════════════════════════════════════════════
// Five groups, and Bug reports set apart (ORG-08). There were nine flat tabs:
// the bar pushed /org sideways at 1024px, its labels wrapped at 1280px, and it
// filed the one triage queue among eight pages of configuration.
//
// A section keeps the id its tab had. `?tab=holidays`, a notification's
// `?tab=bugs` and a stored `hf.org.tab` from before the regroup all still land
// where they pointed: on the section, now inside its group.
type SectionId =
  | 'hours'
  | 'holidays'
  | 'policy'
  | 'types'
  | 'employment'
  | 'departments'
  | 'skills'
  | 'capacity'
  | 'events'
  | 'roles';
type GroupId = 'time' | 'leave' | 'people' | 'access' | 'calendar';
type TabId = GroupId | 'bugs';
// Each glyph is the one UI-CONVENTIONS' icon table gives its meaning. Leave was
// the flag (a correction) and Access the lock (a locked payroll month).
const ORG_GROUPS: { id: GroupId; label: string; icon: IconName; sections: SectionId[] }[] = [
  { id: 'time', label: 'Work time', icon: 'clock', sections: ['hours', 'holidays'] },
  { id: 'leave', label: 'Leave', icon: 'leave', sections: ['policy', 'types'] },
  {
    id: 'people',
    label: 'People setup',
    icon: 'users',
    sections: ['employment', 'departments', 'skills', 'capacity'],
  },
  { id: 'access', label: 'Access', icon: 'key', sections: ['roles'] },
  { id: 'calendar', label: 'Calendar', icon: 'calendar', sections: ['events'] },
];
const SECTION_LABEL: Record<SectionId, string> = {
  hours: 'Office hours',
  holidays: 'Holidays',
  policy: 'Leave policy',
  types: 'Leave types',
  employment: 'Employment',
  departments: 'Departments',
  skills: 'Skills',
  capacity: 'Capacity',
  events: 'Event types',
  roles: 'Roles & access',
};
// Where the Topbar's "Report a bug" reports land. Here rather than /inbox:
// that route is a placeholder for an unbuilt module, and `inbox` is 'view' for
// member and contractor, so the audience would be wrong by construction.
const BUGS_LABEL = 'Bug reports';

/**
 * Which tab to show, and which section of it to bring into view.
 *
 * `asked` is ?tab=, `stored` the last tab from localStorage; each may be a
 * group id or a section's old tab id. A tab the viewer doesn't have (Bug
 * reports without org:full, or anything unknown) falls through to the next
 * candidate, then to Work time — Org used to restore a stored tab unchecked and
 * render an empty page for it. Only a link asks for a section; a restored tab
 * opens at its top.
 */
// Exported for its test. The file already opts out of Fast Refresh (it exports
// yearlyHolidayDates), so the rule has nothing further to protect here.
// eslint-disable-next-line react-refresh/only-export-components
export const resolveOrgTab = (
  asked: string | null,
  stored: string | null,
  canTriage: boolean,
): { tab: TabId; section?: SectionId } => {
  for (const id of [asked, stored]) {
    if (id === 'bugs' && canTriage) return { tab: 'bugs' };
    const group = ORG_GROUPS.find((g) => g.id === id);
    if (group) return { tab: group.id };
    const owner = ORG_GROUPS.find((g) => (g.sections as string[]).includes(id ?? ''));
    if (!owner) continue;
    // The group's first section is already at the top of the page.
    const below = id === asked && owner.sections[0] !== id;
    return below ? { tab: owner.id, section: id as SectionId } : { tab: owner.id };
  }
  return { tab: 'time' };
};

// One section of a group: a heading the cards sit under (they are h3 here), and
// the anchor an old tab link scrolls to.
const OrgSection = ({ id, children }: { id: SectionId; children: ReactNode }) => (
  <section
    id={`org-${id}`}
    aria-labelledby={`org-${id}-title`}
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--hf-space-3)',
      scrollMarginTop: 'var(--hf-space-4)',
    }}
  >
    <h2
      id={`org-${id}-title`}
      style={{
        margin: 0,
        fontSize: 'var(--hf-text-lg)',
        fontWeight: 'var(--hf-weight-medium)',
        color: 'var(--hf-ink)',
      }}
    >
      {SECTION_LABEL[id]}
    </h2>
    {children}
  </section>
);

export function OrgSettingsScreen() {
  const { perms, roleId: myRole, session } = useAuth();
  const editable = can(perms, 'org', 'full');
  const phone = useBelow('md');

  // ?tab= drives it, same as Workspace; a plain /org falls back to the last tab
  // from localStorage, and Work time under that. Bug reports mirrors bug_read
  // exactly (org:full): hr sits at org='edit', and would otherwise get a tab
  // that is permanently empty rather than absent.
  const [orgParams, setOrgParams] = useSearchParams();
  const { tab, section } = resolveOrgTab(
    orgParams.get('tab'),
    localStorage.getItem('hf.org.tab'),
    editable,
  );
  const setTab = (t: TabId) =>
    setOrgParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', t);
        // A role opened on a phone belongs to Access; coming back opens the list.
        next.delete('role');
        return next;
      },
      { replace: false },
    );
  usePageCrumbs(
    // OrgSettingsScreen returns its "not configured" notice before reaching
    // the tabs when supabase is null, below — no point crumbing a tab whose
    // body never renders.
    supabase ? [{ label: ORG_GROUPS.find((g) => g.id === tab)?.label ?? BUGS_LABEL }] : [],
  );
  // Same registry the sidebar reads, so the tab badge and the nav badge cannot
  // disagree. Keyed on `tab` so switching tabs refetches — resolving a report
  // then leaving the tab shows the new number when you come back.
  const { byQueue } = useAttention(perms, session?.user.id, tab);
  const openBugs = byQueue.bugs ?? 0;
  const [loading, setLoading] = useState(true);
  // A failed read is not an empty organisation. The load acted only on `data`,
  // so a 500 rendered "0 work days · 0h" over a blank tab with nothing to retry,
  // and an admin could fairly conclude the configuration had been wiped (ORG-V3).
  const [loadError, setLoadError] = useState(false);
  const [reloads, setReloads] = useState(0);
  // Every save here reports through the page's toast host: a receipt with Undo,
  // or the failure, which stays until dismissed. It was one pill beside the tabs
  // that said nothing on success, reflowed the tab bar when it appeared, and
  // took a failure away after 2.8 seconds (ORG-02).
  const { push } = useToast();

  const [org, setOrg] = useState<OrgSettings | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permsByRole, setPermsByRole] = useState<Record<string, Record<string, PermLevel>>>({});
  const [headcounts, setHeadcounts] = useState<Record<string, number> | null>(null);
  // profiles_read (20260604000000_rbac_dynamic.sql:142-143) shows the whole
  // roster only at people:view or talent:view. Anyone narrower reads their own
  // row, and a headcount made of it is not a headcount.
  const seesRoster = can(perms, 'people', 'view') || can(perms, 'talent', 'view');
  const [selRole, setSelRole] = useState<string>('owner');
  // What an Undo checks itself against. A burst is made on the first change it
  // carries, so its closures hold the state of that render.
  const live = useRef({ org, leaveTypes, holidays, roles });
  useEffect(() => {
    live.current = { org, leaveTypes, holidays, roles };
  });
  // How many changes each record has had here. A write's Undo, made when it was
  // the last change, only writes while it still is: a holiday's type or a role's
  // place in the tree, changed again since, was silently put back.
  const changes = useRef(new Map<string, number>());
  /** Counts a change to `key`: the answer says whether it is still the last. */
  const change = (key: string) => {
    const n = (changes.current.get(key) ?? 0) + 1;
    changes.current.set(key, n);
    return () => changes.current.get(key) === n;
  };
  /** Refuses an Undo that would break the settings (orgFault): true if it did.
   *  `then`: the settings and leave types as the Undo would leave them. */
  const refuseUndo = (
    what: string,
    then: (now: OrgSettings, types: LeaveType[]) => [OrgSettings, LeaveType[]],
  ) => {
    const { org: now, leaveTypes: types } = live.current;
    const fault = now && undoFault(now, types, ...then(now, types));
    if (fault) push({ tone: 'info', message: `${what} wasn’t put back: ${fault}` });
    return !!fault;
  };

  useEffect(() => {
    localStorage.setItem('hf.org.tab', tab);
  }, [tab]);
  // An old tab link names a section that now sits below another: bring it up.
  // The page scrolls, not the tab bar, so this is scrollIntoView's job.
  useEffect(() => {
    if (section && !loading) document.getElementById(`org-${section}`)?.scrollIntoView?.();
  }, [section, loading]);

  // ── load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      const client = supabase;
      if (!client) return;
      const results = await Promise.all([
        client.from('org_settings').select('*').eq('id', 1).single(),
        client.from('holidays').select('*').order('date'),
        client.from('leave_types').select('*').order('rank'),
        client.from('roles').select('*').order('rank'),
        client.from('role_permissions').select('role_id, module, level'),
        client.from('profiles').select('role_id'),
      ]);
      if (!active) return;
      const [settingsRes, holRes, ltRes, roleRes, rpRes, profRes] = results;

      if (settingsRes.data) {
        const s = settingsRes.data as OrgSettings;
        setOrg({
          ...s,
          policy: {
            ...DEFAULT_POLICY,
            ...(s.policy || {}),
            overtime: { ...DEFAULT_POLICY.overtime, ...(s.policy?.overtime || {}) },
            leaveClash: { ...DEFAULT_POLICY.leaveClash, ...(s.policy?.leaveClash || {}) },
            employment: { ...DEFAULT_POLICY.employment, ...(s.policy?.employment || {}) },
            // One level deeper for deduction: a saved { enabled } alone would
            // otherwise drop the waiver default.
            payroll: {
              ...DEFAULT_POLICY.payroll,
              ...(s.policy?.payroll || {}),
              deduction: {
                ...DEFAULT_POLICY.payroll.deduction,
                ...(s.policy?.payroll?.deduction || {}),
              },
            },
            calendar: { ...DEFAULT_POLICY.calendar, ...(s.policy?.calendar || {}) },
          },
        });
      }
      setHolidays((holRes.data as Holiday[]) ?? []);
      setLeaveTypes((ltRes.data as LeaveType[]) ?? []);
      const roleRows = (roleRes.data as Role[]) ?? [];
      setRoles(roleRows);
      if (roleRows.length)
        setSelRole((prev) => (roleRows.some((r) => r.id === prev) ? prev : roleRows[0].id));

      const map: Record<string, Record<string, PermLevel>> = {};
      ((rpRes.data as RolePerm[]) ?? []).forEach((r) => {
        (map[r.role_id] ||= {})[r.module] = r.level;
      });
      setPermsByRole(map);

      const hc: Record<string, number> = {};
      ((profRes.data as { role_id: string | null }[]) ?? []).forEach((p) => {
        if (p.role_id) hc[p.role_id] = (hc[p.role_id] || 0) + 1;
      });
      setHeadcounts(profRes.error ? null : hc);
      // One answer for the six reads: half a settings page is not one to edit.
      setLoadError(results.some((r) => r.error) || !settingsRes.data);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [reloads]);
  const reload = () => {
    setLoading(true);
    setReloads((n) => n + 1);
  };

  // ── org_settings ────────────────────────────────────────────────────────────
  // Every handler below checks `editable` BEFORE its optimistic setState. It used
  // to check after, so a read-only viewer's change showed as applied, never
  // saved, and vanished on reload. The fieldsets around each section's controls
  // stop the click; this stops anything that gets past them.
  // Made on the first change, like a leave type's: its Undo reads `live`, which a
  // burst made during render (useMemo) may not.
  const orgBurstRef = useRef<Burst<OrgSettings, SaveNote> | null>(null);
  const orgBurst = () => {
    if (orgBurstRef.current) return orgBurstRef.current;
    const put = (values: Partial<OrgSettings>) => setOrg((p) => (p ? { ...p, ...values } : p));
    const burst = createBurst<OrgSettings, SaveNote>(async (delta, before, note, turn) => {
      if (!supabase) return false;
      const what = describeChange(delta, before);
      // guard_event_types (20260917120000_calendar_events.sql) refuses a policy
      // write that drops an event type's id, so a change that added one has no
      // way back to offer. Archiving is how a type is retired.
      const typesWere = before.policy?.calendar.types;
      const addsType =
        !!typesWere &&
        !!delta.policy?.calendar.types.some((t) => !typesWere.some((w) => w.id === t.id));
      const ok = settle(
        push,
        await write(supabase.from('org_settings').update(delta).eq('id', 1)),
        what,
        {
          message: note?.undo ? `${what} put back.` : note?.message,
          // Only the columns nothing has changed since: a newer change to one is
          // settled by its own write.
          rollback: () => put(turn.untouched(before)),
          undo:
            note?.undo || addsType
              ? undefined
              : () => {
                  if (!turn.latest()) return push(changedSince(what));
                  if (refuseUndo(what, (now, types) => [{ ...now, ...before }, types])) return;
                  put(before);
                  void burst.add(before, delta, { undo: true });
                },
        },
      );
      // Every one of these feeds a date or hours calculation elsewhere in the app
      // through useOrgDates(), which caches. Dropped once the write has landed:
      // dropped any earlier, a screen that refetched in between would cache the
      // old row for the rest of the session.
      if (ok && CACHED_ORG_KEYS.some((k) => k in delta)) resetOrgDates();
      return ok;
    });
    return (orgBurstRef.current = burst);
  };
  // Leaving the screen inside the pause must not lose the change.
  useEffect(() => () => orgBurstRef.current?.flush(), []);

  /** Resolves once the write has settled: true if it was saved. */
  const patchOrg = (delta: Partial<OrgSettings>, note?: SaveNote): Promise<boolean> => {
    if (!supabase || !editable || !org) return Promise.resolve(false);
    setOrg((p) => (p ? { ...p, ...delta } : p)); // optimistic
    return orgBurst().add(delta, pick(org, delta), note);
  };
  const patchPolicy = (policy: Policy, note?: SaveNote) => patchOrg({ policy }, note);

  // ── holidays ────────────────────────────────────────────────────────────────
  const addHoliday = async (h: {
    date: string;
    name: string;
    kind: HolidayKind;
    repeat: boolean;
  }): Promise<boolean> => {
    if (!supabase || !editable) return false;
    // Repeat yearly: clone into the next 5 years (holidays has no unique index,
    // so we dedupe against what's already loaded). Against this holiday only: a
    // day can carry several, and deduping against every date skipped a year
    // another holiday held, or all five ("already on that date every year").
    const dates = h.repeat
      ? yearlyHolidayDates(
          h.date,
          new Set(holidays.filter((x) => sameName(x.name, h.name)).map((x) => x.date)),
        )
      : [h.date];
    const failed = (message: string) => {
      push({ tone: 'error', message });
      return false;
    };
    if (!dates.length) return failed(`${h.name} is already on that date every year.`);
    const { data, error } = await supabase
      .from('holidays')
      .insert(dates.map((date) => ({ date, name: h.name, kind: h.kind })))
      .select();
    if (error || !data?.length)
      return failed(`${h.name} wasn’t added: ${error?.message ?? REFUSED}`);
    const added = data as Holiday[];
    setHolidays((prev) => byDate([...prev, ...added]));
    push({
      tone: 'success',
      message: added.length > 1 ? `Added ${h.name} for ${added.length} years.` : `Added ${h.name}.`,
      action: { label: 'Undo', onClick: () => void removeHolidays(added, true) },
    });
    return true;
  };
  const holidayNames = (hs: Holiday[]) => (hs.length > 1 ? `${hs.length} holidays` : hs[0].name);
  // Removing a holiday is undone by adding it again, under a new id.
  const removeHolidays = async (gone: Holiday[], undoing = false) => {
    if (!supabase || !editable || !gone.length) return;
    const ids = gone.map((h) => h.id);
    const what = holidayNames(gone);
    setHolidays((prev) => prev.filter((h) => !ids.includes(h.id))); // optimistic
    settle(push, await write(supabase.from('holidays').delete().in('id', ids)), what, {
      verb: 'removed',
      message: undoing ? `${what} taken off again.` : `Removed ${what}.`,
      rollback: () => setHolidays((prev) => byDate([...prev, ...gone])),
      undo: undoing ? undefined : () => void restoreHolidays(gone),
    });
  };
  const restoreHolidays = async (gone: Holiday[]) => {
    if (!supabase || !editable) return;
    const what = holidayNames(gone);
    const { data, error } = await supabase
      .from('holidays')
      .insert(gone.map(({ date, name, kind }) => ({ date, name, kind })))
      .select();
    if (error || !data?.length)
      return push({
        tone: 'error',
        message: `${what} wasn’t put back: ${error?.message ?? REFUSED}`,
      });
    setHolidays((prev) => byDate([...prev, ...(data as Holiday[])]));
    push({ tone: 'success', message: `${what} put back.` });
  };
  const removeHoliday = (id: string) => void removeHolidays(holidays.filter((h) => h.id === id));
  const setHolidayKind = async (h: Holiday, kind: HolidayKind, undoing = false) => {
    if (!supabase || !editable || kind === h.kind) return;
    const put = (k: HolidayKind) =>
      setHolidays((prev) => prev.map((x) => (x.id === h.id ? { ...x, kind: k } : x)));
    put(kind); // optimistic
    const latest = change(`holiday.${h.id}`);
    settle(push, await write(supabase.from('holidays').update({ kind }).eq('id', h.id)), h.name, {
      message: `${h.name} is ${undoing ? 'back to' : 'now'} ${ORG_HOLIDAY_KINDS[kind].label.toLowerCase()}.`,
      rollback: () => put(h.kind),
      undo: undoing
        ? undefined
        : () => {
            // Its update would match no row, and "your role can’t change this".
            if (!live.current.holidays.some((x) => x.id === h.id))
              return push({
                tone: 'info',
                message: `${h.name} was removed after this, so nothing was undone.`,
              });
            if (!latest()) return push(changedSince(h.name));
            void setHolidayKind({ ...h, kind }, h.kind, true);
          },
    });
  };

  // ── leave types ─────────────────────────────────────────────────────────────
  const addLeaveType = async (t: Omit<LeaveType, 'rank'>): Promise<boolean> => {
    if (!supabase || !editable) return false;
    const row = { ...t, rank: leaveTypes.length };
    const { data, error } = await supabase.from('leave_types').insert(row).select().single();
    if (error || !data) {
      push({ tone: 'error', message: `${t.label} wasn’t added: ${error?.message ?? REFUSED}` });
      return false;
    }
    setLeaveTypes((prev) => [...prev, data as LeaveType]);
    push({ tone: 'success', message: `Added ${t.label}.` });
    return true;
  };
  // One burst per type: a run of ± clicks on its days is one write and one Undo.
  const typeBursts = useRef(new Map<string, Burst<LeaveType, SaveNote>>());
  useEffect(() => {
    const bursts = typeBursts.current;
    return () => bursts.forEach((b) => b.flush());
  }, []);
  /** Resolves once the write has settled: true if it was saved. */
  const updateLeaveType = (
    id: string,
    delta: Partial<LeaveType>,
    message?: string,
  ): Promise<boolean> => {
    const t = leaveTypes.find((x) => x.id === id);
    if (!supabase || !editable || !t) return Promise.resolve(false);
    const sb = supabase;
    const put = (values: Partial<LeaveType>) =>
      setLeaveTypes((prev) => prev.map((x) => (x.id === id ? { ...x, ...values } : x)));
    let burst = typeBursts.current.get(id);
    if (!burst) {
      const made = createBurst<LeaveType, SaveNote>(async (d, before, note, turn) => {
        const what = note?.what ?? 'Leave type';
        return settle(push, await write(sb.from('leave_types').update(d).eq('id', id)), what, {
          message: note?.undo ? `${what} put back.` : note?.message,
          rollback: () => put(turn.untouched(before)),
          undo: note?.undo
            ? undefined
            : () => {
                if (!turn.latest()) return push(changedSince(what));
                const undone = (
                  now: OrgSettings,
                  types: LeaveType[],
                ): [OrgSettings, LeaveType[]] => [
                  now,
                  types.map((x) => (x.id === id ? { ...x, ...before } : x)),
                ];
                if (refuseUndo(what, undone)) return;
                put(before);
                void made.add(before, d, { what, undo: true });
              },
        });
      });
      typeBursts.current.set(id, (burst = made));
    }
    put(delta); // optimistic
    return burst.add(delta, pick(t, delta), { what: delta.label ?? t.label, message });
  };
  // No removeLeaveType: a type is archived (an update like any other), never deleted.

  // ── roles & permissions ─────────────────────────────────────────────────────
  const roleName = (id: string | null) => roles.find((r) => r.id === id)?.label ?? id ?? '';
  const addRole = async ({
    label,
    desc,
    parent,
    tone,
  }: {
    label: string;
    desc: string;
    parent: string;
    tone: string;
  }): Promise<boolean> => {
    if (!supabase || !editable || !label.trim()) return false;
    const failed = (message: string) => {
      push({ tone: 'error', message });
      return false;
    };
    const id = roleSlug(label) || `role-${Date.now()}`;
    if (roles.some((r) => r.id === id)) return failed('A role with that name already exists.');
    const description = desc.trim() || 'Custom role';
    const { error: roleErr } = await supabase.from('roles').insert({
      id,
      label: label.trim(),
      tone,
      description,
      parent_id: parent,
      is_custom: true,
      rank: 100 + roles.length,
    });
    if (roleErr) return failed(`${label.trim()} wasn’t created: ${roleErr.message}`);
    // new roles start with their manager's access
    const parentPerms = permsByRole[parent] || {};
    const seed = MODULES.map((m) => ({
      role_id: id,
      module: m,
      level: (parentPerms[m] ?? 'none') as PermLevel,
    }));
    // The role row exists from here on; a failed seed leaves it with no access
    // rows, which the matrix must show as such rather than as the parent's.
    const { error: seedErr } = await supabase.from('role_permissions').upsert(seed);
    setRoles((prev) => [
      ...prev,
      {
        id,
        label: label.trim(),
        tone,
        description,
        parent_id: parent,
        is_custom: true,
        rank: 100 + roles.length,
      },
    ]);
    setPermsByRole((prev) => ({
      ...prev,
      [id]: seedErr ? {} : Object.fromEntries(seed.map((s) => [s.module, s.level])),
    }));
    setSelRole(id);
    push(
      seedErr
        ? {
            tone: 'error',
            message: `${label.trim()} was created, but its manager’s access could not be copied: ${seedErr.message}`,
          }
        : {
            tone: 'success',
            message: `Created ${label.trim()} with ${roleName(parent)}’s access.`,
          },
    );
    // The role exists either way, so the form has nothing left to keep.
    return true;
  };
  // How many writes each role's module has had. A receipt's Undo, and a failed
  // write's rollback, touch only the levels nothing has written since. Lower
  // People Full → View → None, undo the first receipt, and it wrote Full over the
  // None: salaries reopened with no confirm, the later change silently undone.
  const permTurns = useRef(new Map<string, number>());
  // One access write at a time, in the order made, as createBurst sends them.
  // Two upserts to one level in flight together could land out of order: raise
  // People to Full, lower it to None straight after, and Full could be what was
  // stored under a None on screen.
  const permQueue = useRef<Promise<unknown>>(Promise.resolve());
  // Each queued write's `before`. One that fails hands what is stored on to the
  // writes made after it, so their rollback, Undo and raise dialog tell the truth.
  const permWaiting = useRef(new Set<{ roleId: string; before: Levels }>());
  // An Undo that raises access asks first, as the raise itself would have.
  const [undoRaise, setUndoRaise] = useState<Pending | null>(null);
  // The toast host outlives this screen: an Undo pressed after leaving it has no
  // dialog here to ask in.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // `before` is passed, not read from state, so the Undo a receipt carries does
  // not depend on which render's closure it kept.
  const savePerms = async (
    roleId: string,
    next: Levels,
    before: Levels,
    message: string,
    undoing = false,
  ) => {
    if (roleId === 'owner') return; // protected superuser role
    if (!supabase || !editable) return;
    const sb = supabase;
    // A copy: a write made before this one that fails rewrites it.
    const mine = { roleId, before: { ...before } };
    const was = mine.before;
    const put = (levels: Levels) =>
      setPermsByRole((prev) => ({ ...prev, [roleId]: { ...(prev[roleId] || {}), ...levels } }));
    put(next); // optimistic
    const turns = permTurns.current;
    const turn = (m: string) => `${roleId}.${m}`;
    for (const m of Object.keys(next)) turns.set(turn(m), (turns.get(turn(m)) ?? 0) + 1);
    const at = new Map(Object.keys(next).map((m) => [m, turns.get(turn(m))]));
    const fresh = (m: string) => turns.get(turn(m)) === at.get(m);
    const latest = () => Object.keys(next).every(fresh);
    const rows = Object.entries(next).map(([module, level]) => ({
      role_id: roleId,
      module,
      level,
    }));
    const who = roleName(roleId);
    // Taking Organisation: Full from the role you hold ends your own right to
    // give it back: the database would refuse the Undo, so none is offered.
    const lockedOut = roleId === myRole && !!next.org && next.org !== 'full';
    const what = `${who}’s access`;
    // Checked again on confirm: the dialog is not the last word on what is stored.
    const putBack = () =>
      latest()
        ? void savePerms(roleId, was, next, `${what} put back.`, true)
        : push(changedSince(what));
    permWaiting.current.add(mine);
    const job = permQueue.current.then(() =>
      // role_permissions has no `id`: its key is (role_id, module).
      write(sb.from('role_permissions').upsert(rows), 'role_id'),
    );
    permQueue.current = job.catch(() => {});
    const result = await job;
    permWaiting.current.delete(mine);
    if (!result.ok)
      for (const w of permWaiting.current)
        if (w.roleId === roleId)
          for (const m of Object.keys(next)) if (m in w.before) w.before[m] = was[m];
    settle(push, result, what, {
      message: lockedOut ? `${message} You can no longer change these settings.` : message,
      rollback: () => put(Object.fromEntries(Object.entries(was).filter(([m]) => fresh(m)))),
      undo:
        undoing || lockedOut
          ? undefined
          : () => {
              // While latest(), the levels stored now are `next`.
              const ask =
                latest() &&
                askRaise(
                  { title: `Raise ${who}’s access?`, confirmLabel: 'Put it back', run: putBack },
                  next,
                  was,
                  seesRoster && headcounts ? (headcounts[roleId] ?? 0) : null,
                );
              if (!ask) return putBack();
              if (!mounted.current)
                return push({
                  tone: 'info',
                  message: `Open Organisation settings to put ${what} back.`,
                });
              setUndoRaise(ask);
            },
    });
  };
  const levelsOf = (roleId: string, modules: readonly string[]): Levels =>
    Object.fromEntries(modules.map((m) => [m, permsByRole[roleId]?.[m] ?? 'none']));
  const setPerm = (roleId: string, module: string, level: PermLevel) =>
    void savePerms(
      roleId,
      { [module]: level },
      levelsOf(roleId, [module]),
      `${roleName(roleId)}: ${ORG_MODULE_META[module]?.label ?? module} access is now ${level}.`,
    );
  const copyParentAccess = (roleId: string) => {
    const parent = roles.find((r) => r.id === roleId)?.parent_id;
    if (!parent) return;
    void savePerms(
      roleId,
      levelsOf(parent, MODULES),
      levelsOf(roleId, MODULES),
      `${roleName(roleId)} now has ${roleName(parent)}’s access.`,
    );
  };
  const moveRole = async (
    roleId: string,
    to: string | null,
    from: string | null,
    undoing = false,
  ) => {
    if (!supabase || !editable) return;
    const who = roleName(roleId);
    // Against the tree as it is now, not as the picker or the receipt saw it.
    // Move Contractor under HR, Lead under Contractor, then Undo the first:
    // Contractor went back under Lead, a cycle the database took, after which
    // the branch hung off nothing.
    if (to && [roleId, ...descendantsOf(live.current.roles, roleId)].includes(to))
      return push({
        tone: 'info',
        message: `${who} wasn’t ${undoing ? 'put back' : 'moved'}: ${roleName(to)} reports into it.`,
      });
    const put = (parent_id: string | null) =>
      setRoles((prev) => prev.map((r) => (r.id === roleId ? { ...r, parent_id } : r)));
    put(to); // optimistic
    const latest = change(`role.${roleId}`);
    settle(
      push,
      await write(supabase.from('roles').update({ parent_id: to }).eq('id', roleId)),
      who,
      {
        message: to
          ? `${who} now reports into ${roleName(to)}.`
          : `${who} is now a top-level role.`,
        rollback: () => put(from),
        undo: undoing
          ? undefined
          : () => (latest() ? void moveRole(roleId, from, to, true) : push(changedSince(who))),
      },
    );
  };
  const reparentRole = (roleId: string, parentId: string | null) =>
    void moveRole(roleId, parentId, roles.find((r) => r.id === roleId)?.parent_id ?? null);
  const removeRole = async (id: string) => {
    const gone = roles.find((r) => r.id === id);
    if (!supabase || !editable || !gone) return;
    setRoles((prev) => prev.filter((r) => r.id !== id)); // optimistic
    setSelRole(gone.parent_id ?? 'owner');
    settle(push, await write(supabase.from('roles').delete().eq('id', id)), gone.label, {
      verb: 'deleted',
      message: `Deleted ${gone.label}.`,
      // The role was taken off the tree before the answer came back, and a
      // refused delete left it off until reload. Put it back, selected.
      rollback: () => {
        setRoles((prev) => [...prev, gone].sort((a, b) => a.rank - b.rank));
        setSelRole(id);
      },
    });
  };

  if (!supabase) {
    return (
      <div style={{ ...noteStyle, padding: 'var(--hf-space-6)' }}>
        Supabase isn’t configured — set <Mono>VITE_SUPABASE_URL</Mono> /{' '}
        <Mono>VITE_SUPABASE_ANON_KEY</Mono>.
      </div>
    );
  }
  if (loading) {
    return (
      <div role="status" style={{ ...noteStyle, padding: 'var(--hf-space-6)' }}>
        Loading organisation…
      </div>
    );
  }
  const zone = !org ? '' : org.tz_iana ? tzLabel(org.tz_iana) : org.timezone;
  // "Hyprflow · Hyprflow Design Pvt. Ltd." was a stutter: a legal name that
  // starts with the name says both, so only the rest of it follows the name.
  const legal = !org?.legal_name
    ? ''
    : org.legal_name.startsWith(org.name)
      ? org.legal_name.slice(org.name.length)
      : ` · ${org.legal_name}`;

  return (
    <div
      style={{
        // A phone's gutter: 24px a side was 15% of a 320px screen.
        padding: phone ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        maxWidth: 1240,
        margin: '0 auto',
      }}
    >
      {/* One line of context. This was a hero band on every tab: a logo tile,
          an "Organisation" badge under a Topbar that already says so, and four
          stats in four hues that meant nothing, each repeating a card below it
          (ORG-19). Then an 18px display name over a mono line, which a phone
          wrapped to three. It is a card's meta now, not a heading: whose
          settings these are and the zone their days fall in. One line at any
          width: the legal name gives way first (an ellipsis on screen, the
          whole line in its title), the zone never does. The Topbar's title is
          the page's one h1. */}
      {(org || !editable) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hf-space-2) var(--hf-space-3)',
            flexWrap: 'wrap',
          }}
        >
          {org && (
            <p
              title={`${org.name}${legal} · ${zone}`}
              style={{ ...metaStyle, margin: 0, display: 'flex', flex: '1 1 20rem', minWidth: 0 }}
            >
              <span style={oneLine}>
                <span style={{ color: 'var(--hf-ink-2)' }}>{org.name}</span>
                {legal}
              </span>
              {/* The zone doesn't shrink, so the names give way first; capped
                  at the line, it is cut only when it alone is wider (a long
                  IANA name on a 320px phone). Any shrink at all, however
                  weighted, cost it its last character. A phone drops the IANA
                  name, which the abbreviation and offset say again: at 390 it
                  cut the legal name to "Hyprflow…", and at 320 left none. */}
              <span style={{ ...oneLine, flexShrink: 0, maxWidth: '100%', whiteSpace: 'pre' }}>
                {' · '}
                {phone ? zone.split(' · ').pop() : zone}
              </span>
            </p>
          )}
          {!editable && (
            <Badge tone="neutral">Read-only · changing these needs Organisation: Full</Badge>
          )}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          // The groups bar scrolls; Bug reports wraps under it. On a phone the
          // wrapped row's 24px was a gap of dead space above the settings, and
          // 12px lets the two bars share a row at 200% zoom (720px).
          gap: phone ? 'var(--hf-space-3)' : 'var(--hf-space-5)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, maxWidth: '100%' }}>
          <Tabs
            label="Organisation settings"
            tabs={ORG_GROUPS}
            value={tab === 'bugs' ? undefined : tab}
            onChange={setTab}
          />
        </div>
        {/* Its own bar, after a gap: the one queue on this screen is not a
            sixth kind of setting. */}
        {editable && (
          <Tabs
            label="Triage"
            tabs={[
              {
                id: 'bugs' as const,
                label: BUGS_LABEL,
                icon: 'inbox' as const,
                // The sidebar badges Organisation with this same number; without
                // it here you had to open the tab to find out which section the
                // work was in. Same registry, so the two cannot disagree.
                badge: <AttentionCount n={openBugs} label="open bug reports" />,
              },
            ]}
            value={tab === 'bugs' ? 'bugs' : undefined}
            onChange={setTab}
          />
        )}
      </div>

      {/* ── Tab body ── */}
      {/* Read-only viewers (no org:full) get disabled controls, not controls whose
          changes look applied and then vanish. Sections that are all settings sit
          in one fieldset; Holidays and Roles take `editable` because their year
          picker and role list are for looking, and must stay usable. */}
      {tab === 'bugs' ? (
        <OrgBugsTab />
      ) : !org || loadError ? (
        <LoadError what="organisation settings" onRetry={reload} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-6)' }}>
          {tab === 'time' && (
            <>
              <OrgSection id="hours">
                <fieldset disabled={!editable} style={bareFieldset}>
                  <OrgHoursTab org={org} patch={patchOrg} />
                  <OrgEditDayCard policy={org.policy} patch={patchPolicy} />
                </fieldset>
              </OrgSection>
              <OrgSection id="holidays">
                <OrgHolidaysTab
                  holidays={holidays}
                  weekStartsOn={weekStartIndex(org.week_start)}
                  workdays={org.workdays}
                  tz={org.tz_iana}
                  editable={editable}
                  onAdd={addHoliday}
                  onRemove={removeHoliday}
                  onSetKind={(h, kind) => void setHolidayKind(h, kind)}
                />
              </OrgSection>
            </>
          )}
          {tab === 'leave' && (
            <>
              <OrgSection id="policy">
                <fieldset disabled={!editable} style={bareFieldset}>
                  <OrgPolicyTab policy={org.policy} leaveTypes={leaveTypes} patch={patchPolicy} />
                </fieldset>
              </OrgSection>
              <OrgSection id="types">
                <fieldset disabled={!editable} style={bareFieldset}>
                  <OrgLeaveTypesTab
                    leaveTypes={leaveTypes}
                    editable={editable}
                    overtimeTarget={
                      org.policy.overtime.enabled ? org.policy.overtime.targetType : undefined
                    }
                    onAdd={addLeaveType}
                    onUpdate={updateLeaveType}
                  />
                </fieldset>
              </OrgSection>
            </>
          )}
          {tab === 'people' && (
            <>
              <OrgSection id="employment">
                <fieldset disabled={!editable} style={bareFieldset}>
                  <OrgEmploymentTab policy={org.policy} patch={patchPolicy} />
                </fieldset>
              </OrgSection>
              <OrgSection id="departments">
                <OrgDepartmentsTab editable={editable} seesRoster={seesRoster} />
              </OrgSection>
              <OrgSection id="skills">
                <OrgSkillsTab editable={editable} />
              </OrgSection>
              <OrgSection id="capacity">
                <fieldset disabled={!editable} style={bareFieldset}>
                  <OrgCapacityTab policy={org.policy} patch={patchPolicy} />
                </fieldset>
              </OrgSection>
            </>
          )}
          {tab === 'access' && (
            <OrgSection id="roles">
              <OrgRolesTab
                editable={editable}
                roles={roles}
                permsByRole={permsByRole}
                headcounts={seesRoster ? headcounts : null}
                selId={selRole}
                setSelId={setSelRole}
                onAddRole={addRole}
                onSetPerm={setPerm}
                onReparent={reparentRole}
                onCopyParent={copyParentAccess}
                onRemoveRole={removeRole}
              />
            </OrgSection>
          )}
          {tab === 'calendar' && (
            <OrgSection id="events">
              <OrgEventTypesTab
                types={org.policy.calendar.types}
                links={org.policy.calendar.links}
                editable={editable}
                patch={(calendar, message) =>
                  patchPolicy(
                    { ...org.policy, calendar: { ...org.policy.calendar, ...calendar } },
                    { message },
                  )
                }
              />
            </OrgSection>
          )}
        </div>
      )}
      {/* Here, not in the Access tab: a receipt's Undo outlives a tab switch. */}
      <ConfirmDialog
        open={!!undoRaise}
        title={undoRaise?.title ?? ''}
        consequence={undoRaise?.consequence ?? []}
        confirmLabel={undoRaise?.confirmLabel ?? ''}
        tone={undoRaise?.tone}
        onCancel={() => setUndoRaise(null)}
        onConfirm={() => {
          undoRaise?.run();
          setUndoRaise(null);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Departments — the managed list profiles.department_id points at
// (20260917100000_departments.sql). There is no delete: archiving keeps the
// department on the people already in it and takes it off every picker.
// ─────────────────────────────────────────────────────────────────────────────
// One row of a list whose items are archived, never deleted: departments and
// event types. Leave types carry more controls and build their own.
const archivableRow = (last: boolean): CSSProperties => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-3) 0',
  borderBottom: last ? 'none' : '1px solid var(--hf-rule)',
});
const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function OrgDepartmentsTab({
  editable,
  seesRoster,
}: {
  editable: boolean;
  seesRoster: boolean;
}) {
  const { push } = useToast();
  const coarse = useCoarse();
  const [rows, setRows] = useState<Department[]>([]);
  // department id -> active people in it. The DB guard refuses to archive while
  // this is non-zero; the count here only explains why the button is off.
  // null = unknown: profiles_read (20260604000000_rbac_dynamic.sql:142-143) shows
  // the whole roster only at people:view or talent:view, so anyone narrower would
  // count just themselves and see Archive enabled for a department it will refuse.
  const [heads, setHeads] = useState<Record<string, number> | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [draft, setDraft] = useState('');
  // What the database said about the last Add, shown on the field it is about.
  const [addError, setAddError] = useState<string>();
  const [adding, submit] = useSubmit();
  const [open, setOpen] = useState(false);
  const opener = useId();
  const close = () => {
    setOpen(false);
    setDraft('');
    setAddError(undefined);
  };

  const load = useCallback(async () => {
    if (!supabase) return setState('ready');
    const sb = supabase;
    const [depRes, profRes] = await Promise.all([
      sb.from('departments').select('id, name, archived').order('name'),
      seesRoster ? sb.from('profiles').select('department_id, status') : null,
    ]);
    // A failed read is not "No departments yet" (UI-CONVENTIONS §7): the error
    // used to sit above that very sentence.
    if (depRes.error) return setState('failed');
    // Archived last. sort is stable, so both groups keep the name order.
    const list = (depRes.data as Department[] | null) ?? [];
    setRows(list.sort((a, b) => Number(a.archived) - Number(b.archived)));
    if (!profRes || profRes.error) {
      setHeads(null);
    } else {
      const counts: Record<string, number> = {};
      type Head = { department_id: string | null; status: string };
      for (const p of (profRes.data as Head[] | null) ?? []) {
        if (p.department_id && p.status === 'active')
          counts[p.department_id] = (counts[p.department_id] ?? 0) + 1;
      }
      setHeads(counts);
    }
    setState('ready');
  }, [seesRoster]);

  useEffect(() => {
    void load();
  }, [load]);

  // Not optimistic: a row changes only after the DB takes the write, so an
  // archive the guard refuses never shows as archived. Resolves to why it was
  // not saved, or to nothing when it was.
  const save = async (q: Parameters<typeof write>[0]): Promise<string | undefined> => {
    const r = await write(q);
    if (!r.ok) {
      if (r.reason === 'refused') return 'Your role can’t change departments.';
      // 23505 is the lower(btrim(name)) unique index; the raw text names the index.
      return r.error.code === '23505'
        ? 'A department with that name already exists.'
        : r.error.message;
    }
    resetDepartments();
    await load();
    return undefined;
  };

  const name = draft.trim();
  // Not while it is being added: the list reloads with the new department a
  // moment before the form clears, and the draft would read as a clash with it.
  const taken = !adding && rows.some((d) => sameName(d.name, name));
  const add = async () => {
    if (!supabase || !editable || !name || taken) return;
    const sb = supabase;
    // A second Enter while the first insert was on its way sent another, which
    // the unique index refused under a field the first had already cleared.
    const added = await submit(async () => {
      const error = await save(sb.from('departments').insert({ name }));
      setAddError(error);
      return !error;
    });
    if (!added) return;
    close();
    push({ tone: 'success', message: `Added ${name}.` });
  };
  const rename = async (d: Department, v: string): Promise<SaveResult> => {
    const next = v.trim();
    if (!supabase || !editable) return {};
    if (!next) return { error: 'A department needs a name.' };
    return {
      error: await save(supabase.from('departments').update({ name: next }).eq('id', d.id)),
    };
  };
  const setArchived = async (d: Department, archived: boolean, undoing = false) => {
    if (!supabase || !editable) return;
    const error = await save(supabase.from('departments').update({ archived }).eq('id', d.id));
    if (error)
      return push({
        tone: 'error',
        message: `${d.name} wasn’t ${archived ? 'archived' : 'unarchived'}: ${error}`,
      });
    push({
      tone: 'success',
      message: archived ? `Archived ${d.name}.` : `${d.name} is back in use.`,
      action: undoing
        ? undefined
        : { label: 'Undo', onClick: () => void setArchived(d, !archived, true) },
    });
  };

  if (state === 'loading')
    return (
      <div role="status" style={noteStyle}>
        Loading departments…
      </div>
    );
  if (state === 'failed') return <LoadError what="departments" onRetry={() => void load()} />;

  return (
    // No title, as Leave types: it was the section's title again.
    <PCard level={3}>
      <ListHead count={listCount(rows, 'department')}>
        {editable && !open && (
          <AddOpener id={opener} onClick={() => setOpen(true)}>
            Add department
          </AddOpener>
        )}
      </ListHead>
      {editable && open && (
        <DisclosedForm opener={opener}>
          <form
            aria-label="Add department"
            className="hf-panel"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
            style={addPanel}
          >
            <Field
              label="Name"
              required
              error={taken ? 'A department with that name already exists.' : addError}
              style={{ maxWidth: '24rem' }}
            >
              <TextInput
                value={draft}
                onChange={(v) => {
                  setDraft(v);
                  setAddError(undefined);
                }}
                placeholder="e.g. Production"
              />
            </Field>
            <AddActions
              busy={adding}
              reason={name ? undefined : 'Needs a name before it can be added.'}
              blocked={taken}
              onCancel={close}
            >
              Add department
            </AddActions>
          </form>
        </DisclosedForm>
      )}
      {rows.length === 0 && (
        <EmptyState title="No departments yet">
          {editable ? 'Add the first one with Add department.' : undefined}
        </EmptyState>
      )}
      {rows.map((d, i) => {
        const n = heads?.[d.id] ?? 0;
        const people = !heads
          ? ''
          : n
            ? `${n} active ${n === 1 ? 'person' : 'people'}`
            : 'No active people';
        const blocked = !d.archived && n > 0;
        return (
          <div key={d.id} style={archivableRow(i === rows.length - 1)}>
            <div
              style={{
                flex: '1 1 10rem',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hf-space-2)',
                minWidth: 0,
              }}
            >
              {/* Without a save handler it is plain text: nothing to click
                  for a viewer who can't rename. A refused rename keeps the
                  draft and says why, beside it. */}
              <EditableField
                label={`Name of ${d.name}`}
                hideLabel
                value={d.name}
                onSaveAsync={editable ? (v) => rename(d, v) : undefined}
              />
              {d.archived && <Badge tone="neutral">archived</Badge>}
            </div>
            <span style={noteStyle}>
              {blocked && editable ? `${people} · move them first` : people}
            </span>
            {/* Archive only where it can act: six disabled buttons each sat
                beside the note that said "move them first" (critique
                2026-10-06 #5). */}
            {editable &&
              !blocked &&
              (d.archived ? (
                <Button
                  size="sm"
                  variant="ghost"
                  style={rowAction(coarse)}
                  onClick={() => void setArchived(d, false)}
                >
                  Unarchive
                </Button>
              ) : (
                <Button
                  size="sm"
                  style={rowAction(coarse)}
                  onClick={() => void setArchived(d, true)}
                >
                  Archive
                </Button>
              ))}
          </div>
        );
      })}
      <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
        An archived department stays on the people already in it but can’t be picked for anyone new.
      </div>
    </PCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Skills — the org's one skill list (owner, 2026-10-13), which people are rated
// against at four levels. Owner/admin only (skills_cat_insert/_update at
// org:full, 20261013120000_capacity_v1.sql). Archived, never deleted: a delete
// would take people's levels and projects' skill needs with it.
// ─────────────────────────────────────────────────────────────────────────────
type Skill = { id: string; name: string; archived: boolean };

export function OrgSkillsTab({ editable }: { editable: boolean }) {
  const { push } = useToast();
  const coarse = useCoarse();
  const [rows, setRows] = useState<Skill[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [draft, setDraft] = useState('');
  const [addError, setAddError] = useState<string>();
  const [adding, submit] = useSubmit();
  const [open, setOpen] = useState(false);
  const opener = useId();
  const close = () => {
    setOpen(false);
    setDraft('');
    setAddError(undefined);
  };

  const load = useCallback(async () => {
    if (!supabase) return setState('ready');
    const { data, error } = await supabase
      .from('skills')
      .select('id, name, archived')
      .order('name');
    if (error) return setState('failed');
    const list = (data as Skill[] | null) ?? [];
    setRows(list.sort((a, b) => Number(a.archived) - Number(b.archived)));
    setState('ready');
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  // Not optimistic, as Departments: a row changes once the database took it.
  const save = async (q: Parameters<typeof write>[0]): Promise<string | undefined> => {
    const r = await write(q);
    if (!r.ok) {
      if (r.reason === 'refused') return 'Only the owner or an admin can change the skill list.';
      return r.error.code === '23505' ? 'A skill with that name already exists.' : r.error.message;
    }
    await load();
    return undefined;
  };

  const name = draft.trim();
  const taken = !adding && rows.some((d) => sameName(d.name, name));
  const add = async () => {
    if (!supabase || !editable || !name || taken) return;
    const sb = supabase;
    const added = await submit(async () => {
      const error = await save(sb.from('skills').insert({ name }));
      setAddError(error);
      return !error;
    });
    if (!added) return;
    close();
    push({ tone: 'success', message: `Added ${name}.` });
  };
  const rename = async (k: Skill, v: string): Promise<SaveResult> => {
    const next = v.trim();
    if (!supabase || !editable) return {};
    if (!next) return { error: 'A skill needs a name.' };
    return { error: await save(supabase.from('skills').update({ name: next }).eq('id', k.id)) };
  };
  const setArchived = async (k: Skill, archived: boolean, undoing = false) => {
    if (!supabase || !editable) return;
    const error = await save(supabase.from('skills').update({ archived }).eq('id', k.id));
    if (error)
      return push({
        tone: 'error',
        message: `${k.name} wasn’t ${archived ? 'archived' : 'unarchived'}: ${error}`,
      });
    push({
      tone: 'success',
      message: archived ? `Archived ${k.name}.` : `${k.name} is back in use.`,
      action: undoing
        ? undefined
        : { label: 'Undo', onClick: () => void setArchived(k, !archived, true) },
    });
  };

  if (state === 'loading')
    return (
      <div role="status" style={noteStyle}>
        Loading skills…
      </div>
    );
  if (state === 'failed') return <LoadError what="skills" onRetry={() => void load()} />;

  return (
    <PCard level={3}>
      <ListHead count={listCount(rows, 'skill')}>
        {editable && !open && (
          <AddOpener id={opener} onClick={() => setOpen(true)}>
            Add skill
          </AddOpener>
        )}
      </ListHead>
      {editable && open && (
        <DisclosedForm opener={opener}>
          <form
            aria-label="Add skill"
            className="hf-panel"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
            style={addPanel}
          >
            <Field
              label="Name"
              required
              error={taken ? 'A skill with that name already exists.' : addError}
              style={{ maxWidth: '24rem' }}
            >
              <TextInput
                value={draft}
                onChange={(v) => {
                  setDraft(v);
                  setAddError(undefined);
                }}
                placeholder="e.g. Motion design"
              />
            </Field>
            <AddActions
              busy={adding}
              reason={name ? undefined : 'Needs a name before it can be added.'}
              blocked={taken}
              onCancel={close}
            >
              Add skill
            </AddActions>
          </form>
        </DisclosedForm>
      )}
      {rows.length === 0 && (
        <EmptyState title="No skills yet">
          {editable ? 'Add the first one with Add skill.' : undefined}
        </EmptyState>
      )}
      {rows.map((k, i) => (
        <div key={k.id} style={archivableRow(i === rows.length - 1)}>
          <div
            style={{
              flex: '1 1 10rem',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              minWidth: 0,
            }}
          >
            <EditableField
              label={`Name of ${k.name}`}
              hideLabel
              value={k.name}
              onSaveAsync={editable ? (v) => rename(k, v) : undefined}
            />
            {k.archived && <Badge tone="neutral">archived</Badge>}
          </div>
          {editable &&
            (k.archived ? (
              <Button
                size="sm"
                variant="ghost"
                style={rowAction(coarse)}
                onClick={() => void setArchived(k, false)}
              >
                Unarchive
              </Button>
            ) : (
              <Button size="sm" style={rowAction(coarse)} onClick={() => void setArchived(k, true)}>
                Archive
              </Button>
            ))}
        </div>
      ))}
      <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
        People are rated against this list, at four levels, by someone above them in the reporting
        line or by HR. An archived skill comes off their profiles and can’t be picked; the levels
        already set are kept.
      </div>
    </PCard>
  );
}

// Who sees whose free hours on Capacity: policy.capacity.visibility, read by
// capacity_visibility() (20261013120000_capacity_v1.sql), which takes an unset
// key as Chain + HR & admins and an unknown one as Chain.
const VISIBILITY: readonly { value: CapacityVisibility; label: string; desc: string }[] = [
  {
    value: 'chain',
    label: 'Chain',
    desc: 'People see themselves and everyone below them in the reporting line.',
  },
  {
    value: 'chain_hr_admin',
    label: 'Chain + HR & admins',
    desc: 'As Chain, and people with full People access (HR, admins, the owner) see everyone.',
  },
  {
    value: 'everyone',
    label: 'Everyone',
    desc: 'Everyone who can open Capacity sees everyone, including which weeks they are away.',
  },
];

export const OrgCapacityTab = ({
  policy,
  patch,
}: {
  policy: Policy;
  patch: (p: Policy) => void;
}) => {
  const v = policy.capacity?.visibility ?? DEFAULT_CAPACITY_VISIBILITY;
  const target = policy.finance?.targetUtilisation ?? DEFAULT_TARGET_UTILISATION;
  return (
    <div style={cardGrid}>
      <PCard level={3} label="Who sees whose free hours">
        <OrgRow title="Capacity shows" desc={VISIBILITY.find((o) => o.value === v)?.desc} last>
          <SegmentedControl
            label="Who sees whose free hours"
            options={VISIBILITY}
            value={v}
            onChange={(visibility) => patch({ ...policy, capacity: { visibility } })}
          />
        </OrgRow>
      </PCard>
      <PCard level={3} label="Utilisation">
        <OrgRow
          title="Target utilisation"
          desc="Share of available hours (contracted day × working days − leave) people should spend on project timers. The Dashboard measures against it."
          last
        >
          <Stepper
            label="Target utilisation"
            unit="%"
            value={target}
            onChange={(targetUtilisation) =>
              patch({ ...policy, finance: { ...policy.finance, targetUtilisation } })
            }
            min={1}
            max={100}
          />
        </OrgRow>
      </PCard>
    </div>
  );
};

// Revoking is for a leaked link, and nothing brings the links back: it asks
// first, in a dialog that says so. It was a window.confirm.
function RevokeLinks() {
  const { push } = useToast();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    setAsking(false);
    setBusy(true);
    const { error } = await revokeAllLinks();
    setBusy(false);
    push(
      error
        ? { tone: 'error', message: `The links weren’t revoked: ${error.message}` }
        : { tone: 'success', message: 'Every calendar link is revoked.' },
    );
  };
  return (
    <div style={{ marginTop: 'var(--hf-space-3)' }}>
      <Button
        size="sm"
        variant="danger"
        className="hf-hit"
        loading={busy}
        onClick={() => setAsking(true)}
      >
        Revoke every link
      </Button>
      <ConfirmDialog
        open={asking}
        title="Revoke every calendar link?"
        consequence={[
          'Every personal calendar link stops working now, in every calendar app.',
          'This can’t be undone. Everyone has to make a new link from Workspace → Calendar.',
        ]}
        confirmLabel="Revoke every link"
        tone="danger"
        onCancel={() => setAsking(false)}
        onConfirm={() => void revoke()}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Event types — org_settings.policy.calendar.types
// (20260917120000_calendar_events.sql). Events store the id, so a rename shows
// on every event. There is no delete: the DB refuses a policy write that drops
// an id, and archiving takes a type off the event form while the events already
// filed under it keep it. Saved through patchPolicy, which also drops the
// useOrgDates() cache the calendar reads the list from.
//
// policy.calendar.links (20260918100000_calendar_links.sql) lives here too: false
// turns every personal subscription link off at once, without deleting any.
// ─────────────────────────────────────────────────────────────────────────────
export function OrgEventTypesTab({
  types,
  links,
  editable,
  patch: patchCalendar,
}: {
  types: EventType[];
  links: boolean;
  editable: boolean;
  /** `receipt` is what the save says when it lands. Resolves to whether it did. */
  patch: (calendar: { types?: EventType[]; links?: boolean }, receipt: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState('');
  const [adding, submit] = useSubmit();
  const [open, setOpen] = useState(false);
  const opener = useId();
  const close = () => {
    setOpen(false);
    setDraft('');
  };
  const coarse = useCoarse();
  // Archived last. sort is stable, so both groups keep the list order.
  const rows = [...types].sort((a, b) => Number(!!a.archived) - Number(!!b.archived));
  const inUse = rows.filter((t) => !t.archived).length;

  const label = draft.trim();
  // Two types with one name can't be told apart on the event form, and nothing
  // in the database stops it: ids are unique, labels are not. Not while it is
  // being added: the new type is already in the list, ahead of its write.
  const taken = !adding && types.some((t) => sameName(t.label, label));
  const add = async () => {
    if (!editable || !label || taken) return;
    const base =
      label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '') || 'type';
    // Unique among every type, archived included.
    let id = base;
    for (let n = 2; types.some((t) => t.id === id); n++) id = `${base}_${n}`;
    const next = { types: [...types, { id, label }] };
    if (await submit(() => patchCalendar(next, `Added ${label}.`))) close();
  };
  const rename = async (t: EventType, v: string): Promise<SaveResult> => {
    const next = v.trim();
    if (!next) return { error: 'An event type needs a name.' };
    if (types.some((x) => x.id !== t.id && sameName(x.label, next)))
      return { error: 'Another event type already has that name.' };
    const saved = await patchCalendar(
      { types: types.map((x) => (x.id === t.id ? { ...x, label: next } : x)) },
      `Renamed ${t.label} to ${next}.`,
    );
    return saved ? {} : { error: 'Not renamed. The notice on this page says why.' };
  };
  const setArchived = (t: EventType, archived: boolean) =>
    void patchCalendar(
      {
        types: types.map((x) =>
          x.id !== t.id ? x : archived ? { ...x, archived: true } : { id: x.id, label: x.label },
        ),
      },
      archived ? `Archived ${t.label}.` : `${t.label} is back in use.`,
    );

  return (
    <div style={splitRow}>
      <div style={splitCol(5)}>
        {/* No title, as Leave types. */}
        <PCard level={3}>
          <ListHead count={listCount(rows, 'event type')}>
            {editable && !open && (
              <AddOpener id={opener} onClick={() => setOpen(true)}>
                Add event type
              </AddOpener>
            )}
          </ListHead>
          {editable && open && (
            <DisclosedForm opener={opener}>
              <form
                aria-label="Add event type"
                className="hf-panel"
                onSubmit={(e) => {
                  e.preventDefault();
                  void add();
                }}
                style={addPanel}
              >
                <Field
                  label="Name"
                  required
                  error={taken ? 'An event type with that name already exists.' : undefined}
                  style={{ maxWidth: '24rem' }}
                >
                  <TextInput value={draft} onChange={setDraft} placeholder="e.g. Workshop" />
                </Field>
                <AddActions
                  busy={adding}
                  reason={label ? undefined : 'Needs a name before it can be added.'}
                  blocked={taken}
                  onCancel={close}
                >
                  Add event type
                </AddActions>
              </form>
            </DisclosedForm>
          )}
          {rows.length === 0 && <EmptyState title="No event types yet" />}
          {rows.map((t, i) => {
            // An event has to have a type, so the last one in use stays.
            const blocked = !t.archived && inUse <= 1;
            return (
              <div key={t.id} style={archivableRow(i === rows.length - 1)}>
                <div
                  style={{
                    flex: '1 1 10rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hf-space-2)',
                    minWidth: 0,
                  }}
                >
                  <EditableField
                    label={`Name of ${t.label}`}
                    hideLabel
                    value={t.label}
                    onSaveAsync={editable ? (v) => rename(t, v) : undefined}
                  />
                  {t.archived && <Badge tone="neutral">archived</Badge>}
                </div>
                {blocked && editable && <span style={noteStyle}>The last type in use</span>}
                {/* Archive only where it can act, as on Departments. */}
                {editable &&
                  !blocked &&
                  (t.archived ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      style={rowAction(coarse)}
                      onClick={() => setArchived(t, false)}
                    >
                      Unarchive
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      style={rowAction(coarse)}
                      onClick={() => setArchived(t, true)}
                    >
                      Archive
                    </Button>
                  ))}
              </div>
            );
          })}
          <div style={{ ...noteStyle, marginTop: 'var(--hf-space-3)' }}>
            A rename shows on every event of that type. An archived type stays on the events already
            filed under it but can’t be picked for a new one.
          </div>
        </PCard>
      </div>

      <div style={splitCol(3, 280)}>
        <PCard level={3} label="Subscription links">
          <OrgRow
            title="Personal calendar links"
            desc="Everyone with calendar access can subscribe to their own calendar from Google, Apple or Outlook"
            last
          >
            {editable ? (
              <Toggle
                label="Personal calendar links"
                checked={links}
                onChange={(v) =>
                  void patchCalendar(
                    { links: v },
                    `Personal calendar links are ${v ? 'on' : 'paused'}.`,
                  )
                }
              />
            ) : (
              <Badge tone="neutral">{links ? 'on' : 'off'}</Badge>
            )}
          </OrgRow>
          <div style={{ ...noteStyle, marginTop: 'var(--hf-space-2)' }}>
            Off pauses every link; turning it back on revives them. If a link leaked, revoke them
            all instead — everyone makes a new one from Workspace → Calendar.
          </div>
          {editable && <RevokeLinks />}
        </PCard>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bug reports — the triage list for whoever holds org:full.
// ─────────────────────────────────────────────────────────────────────────────
type BugRow = {
  id: string;
  screen: string;
  body: string;
  user_agent: string | null;
  resolved_at: string | null;
  created_at: string;
  screenshot_path: string | null;
  reporter: { full_name: string | null } | { full_name: string | null }[] | null;
};
type BugFilter = 'open' | 'resolved' | 'all';

export function OrgBugsTab() {
  const { push } = useToast();
  const coarse = useCoarse();
  const { dateFormat, tz } = useOrgDates();
  const [rows, setRows] = useState<BugRow[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [filter, setFilter] = useState<BugFilter>('open');
  // path -> signed URL. Minted in one batch per load rather than per row.
  const [shots, setShots] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!supabase) return setState('ready');
    const sb = supabase;
    const { data, error } = await sb
      .from('bug_reports')
      .select(
        'id, screen, body, user_agent, resolved_at, created_at, screenshot_path, reporter:profiles!bug_reports_reporter_fkey(full_name)',
      )
      .order('created_at', { ascending: false });
    // A failed read is not "No bug reports" (UI-CONVENTIONS §7).
    if (error) return setState('failed');
    const list = (data as BugRow[]) ?? [];
    setRows(list);
    setState('ready');

    // 300s rather than a short expiry: an admin reads down a triage list for
    // minutes, and a thumbnail expiring mid-read looks like a broken feature.
    const paths = list.map((r) => r.screenshot_path).filter((p): p is string => !!p);
    if (paths.length) {
      const { data: urls } = await sb.storage.from('bug-screenshots').createSignedUrls(paths, 300);
      const next: Record<string, string> = {};
      for (const u of urls ?? []) if (u.path && u.signedUrl) next[u.path] = u.signedUrl;
      setShots(next);
    }

    // Retention sweep (P2 — GDPR storage limitation): the DB (20260812110000_
    // retention.sql) only ever selects which screenshots on unresolved reports
    // are past the 30-day window — it can never null screenshot_path or delete
    // the object itself, same reason as toggle() below: deleting the
    // storage.objects row in SQL orphans the bytes, still on quota. So this is
    // the same object-first-then-row delete, just batched and run on load
    // instead of on a triage click. Skipped (and retried next load) if the
    // storage delete fails, so a partial failure never orphans the row without
    // its bytes.
    const { data: expired } = await sb.rpc('expired_bug_screenshots');
    const expiredPaths = (expired as string[] | null) ?? [];
    if (expiredPaths.length) {
      const { error: rmErr } = await sb.storage.from('bug-screenshots').remove(expiredPaths);
      if (!rmErr) {
        await sb
          .from('bug_reports')
          .update({ screenshot_path: null })
          .in('screenshot_path', expiredPaths);
        setRows((rs) =>
          rs.map((r) =>
            r.screenshot_path && expiredPaths.includes(r.screenshot_path)
              ? { ...r, screenshot_path: null }
              : r,
          ),
        );
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (r: BugRow) => {
    if (!supabase) return;
    const next = r.resolved_at ? null : new Date().toISOString();

    // Resolving deletes the screenshot — that IS the retention policy, and a
    // screenshot of this app can contain payroll. remove() goes through the
    // Storage API because it is the only thing that removes the bytes: deleting
    // the storage.objects row in SQL orphans them in the backend, still on quota.
    // It is idempotent, so retrying after a partial failure is safe.
    //
    // Object first, row second, and refuse to resolve if the delete failed —
    // that makes "resolved ⇒ no screenshot" an invariant rather than a hope.
    // Note this is one-way: reopening will not bring the screenshot back, which
    // is why the button says so before it is pressed.
    if (next && r.screenshot_path) {
      const { error: rmErr } = await supabase.storage
        .from('bug-screenshots')
        .remove([r.screenshot_path]);
      if (rmErr)
        return push({
          tone: 'error',
          message: `The screenshot couldn’t be deleted, so the report wasn’t marked done: ${rmErr.message}`,
        });
    }

    const after = { ...r, resolved_at: next, screenshot_path: next ? null : r.screenshot_path };
    setRows((rs) => rs.map((x) => (x.id === r.id ? after : x)));
    // resolved_at and screenshot_path are the only columns the UPDATE grant
    // permits, so a typo here fails loudly rather than rewriting someone's report.
    const result = await write(
      supabase
        .from('bug_reports')
        .update({ resolved_at: after.resolved_at, screenshot_path: after.screenshot_path })
        .eq('id', r.id),
    );
    if (!result.ok) {
      push({
        tone: 'error',
        message: `The report wasn’t ${next ? 'marked done' : 'reopened'}: ${result.reason === 'refused' ? REFUSED : result.error.message}`,
      });
      return void load();
    }
    // Under the Open filter a report marked done leaves the list: the receipt
    // is what says where it went, and how to get it back.
    push({
      tone: 'success',
      message: next
        ? `Marked done${r.screenshot_path ? ', and its screenshot deleted' : ''}.`
        : 'Reopened.',
      action: next ? { label: 'Reopen', onClick: () => void toggle(after) } : undefined,
    });
  };

  if (state === 'loading')
    return (
      <div role="status" style={noteStyle}>
        Loading bug reports…
      </div>
    );
  if (state === 'failed') return <LoadError what="bug reports" onRetry={() => void load()} />;

  const open = rows.filter((r) => !r.resolved_at);
  const shown =
    filter === 'open' ? open : filter === 'all' ? rows : rows.filter((r) => r.resolved_at);

  return (
    // The label is the label. It was "Bug reports · N open": the queue's count
    // re-derived in a card header, beside the tab that already carries it
    // (UI-CONVENTIONS §1, ORG-15). The filter's facets are not that count.
    <PCard
      label="Bug reports"
      right={
        <SegmentedControl
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'open', label: 'Open', count: open.length },
            { value: 'resolved', label: 'Resolved', count: rows.length - open.length },
            { value: 'all', label: 'All', count: rows.length },
          ]}
        />
      }
    >
      {shown.length === 0 && (
        <EmptyState
          title={
            rows.length === 0
              ? 'No bug reports yet'
              : filter === 'open'
                ? 'No open bug reports'
                : 'Nothing resolved yet'
          }
        >
          {rows.length === 0
            ? 'Reports sent with “Report a bug” in the top bar land here.'
            : undefined}
        </EmptyState>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {shown.map((r) => {
          const who = Array.isArray(r.reporter) ? r.reporter[0] : r.reporter;
          const name = who?.full_name ?? 'Unknown';
          const done = !!r.resolved_at;
          return (
            <article
              key={r.id}
              style={{ borderTop: '1px solid var(--hf-rule)', padding: 'var(--hf-space-3) 0' }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2)',
                  marginBottom: 'var(--hf-space-2)',
                }}
              >
                <Avatar name={name} size={22} />
                <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
                  {name}
                </span>
                <Badge tone="neutral">{r.screen}</Badge>
                <Mono dim>{fmtDate(r.created_at, dateFormat, { tz })}</Mono>
                {/* Said, not dimmed: at half opacity the text fell to 3:1. */}
                {done && <Badge tone="success">resolved</Badge>}
                <div style={{ marginLeft: 'auto' }}>
                  <Button
                    size="sm"
                    className="hf-hit"
                    variant={done ? 'ghost' : 'secondary'}
                    onClick={() => void toggle(r)}
                  >
                    {done
                      ? 'Reopen'
                      : r.screenshot_path
                        ? 'Mark done · deletes screenshot'
                        : 'Mark done'}
                  </Button>
                </div>
              </div>
              <div
                style={{
                  fontSize: 'var(--hf-text-sm)',
                  lineHeight: 'var(--hf-leading-body)',
                  color: 'var(--hf-ink-2)',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                {r.body}
              </div>
              {/* <img> and nothing else — never window.open, never <iframe> or
                  <object>. An <img> cannot execute script even if a file lies
                  about its type, which is the residual risk the bucket's MIME
                  allow-list doesn't cover. Rendered inline rather than opened in
                  a tab, deliberately: a signed URL in a new tab hands the file
                  the browser's full navigation context. */}
              {r.screenshot_path && shots[r.screenshot_path] && (
                <img
                  src={shots[r.screenshot_path]}
                  alt={`Screenshot from ${who?.full_name ?? 'the reporter'}`}
                  style={{
                    maxWidth: 'min(100%, 360px)',
                    maxHeight: 220,
                    borderRadius: 'var(--hf-radius-md)',
                    border: '1px solid var(--hf-rule-2)',
                    display: 'block',
                    marginTop: 'var(--hf-space-2)',
                  }}
                />
              )}
              {r.user_agent && (
                <details style={{ ...noteStyle, marginTop: 'var(--hf-space-2)' }}>
                  <summary style={{ cursor: 'pointer', ...tapSummary(coarse) }}>
                    Browser details
                  </summary>
                  <span style={{ overflowWrap: 'anywhere' }}>{r.user_agent}</span>
                </details>
              )}
            </article>
          );
        })}
      </div>
    </PCard>
  );
}
